import { useState, useEffect, useRef } from "react";
import { supabase, BRAND, BRAND_LIGHT, saveCandidateToSupabase, unreadBadge, INK, TEXT, MUTED, LINE, SURFACE, NEUTRAL_BG, text, card, btn, btnSm, linkBtn, chip, iconBtn as iconBtnTok, errorText, errorBanner, segWrap, segBtn, pageTitle, SHADOW_POP, SHADOW_SM, DANGER_LINE, WHITE, ROW_LINE, FAINT, BG, STAR, UNREAD, HOVER_BG } from "./constants";
import { Table2, LayoutGrid, FileText, Users, ClipboardList, Sparkles, Network, X } from "lucide-react";
import { Icon, CandidateAvatar, StatusBadge, genUUID, BLANK_QUAL_SECTIONS, evalItemIds, evalAvg5, evalByVector, CloseButton, BackButton, ConfirmBar, HelpButton, MoreMenu, SelectRow } from "./shared";
import OnboardingModal from "./OnboardingModal";
import DocumentsBox from "./DocumentsBox";
import PositionEvaluationTab from "./PositionEvaluationTab";
import MappingTab from "./MappingTab";
import { uploadCandidateCv } from "./lib/cv";
import { aiFetch, authFetch } from "./lib/ai";
import { useIsMobile, MOBILE_TOP } from "./lib/responsive";
import { loadPersonData, findExistingCandidacy, createCandidateFromPerson } from "./lib/personCandidate";

// Inicial gigante de la foto vacía del Wall: hace de imagen, no de texto, por
// eso queda fuera de la escala tipográfica (única excepción, 31/10)
const WALL_INITIAL = 52;

// Alto común de todos los boxes de la fila superior: métricas, documentos y
// recruiter chat. Sin esto, cada uno crecía según su contenido — y el de
// Interested cambiaba de tamaño al aparecer el botón Save.
export const BOX_H = 62;

// Lee un PDF como base64 (sin el prefijo data:) para mandarlo al proxy de
// Anthropic como documento.
const pdfToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1]);
  r.onerror = () => rej(new Error("Read failed"));
  r.readAsDataURL(file);
});

const CV_EXTRACT_PROMPT = "You extract contact and current-role data from a CV. Return ONLY a valid JSON object with these exact keys: name, company, currentPosition, city, email, phone, linkedin, yearsExperience. company and currentPosition are the CURRENT (most recent) employer and job title. linkedin is the full profile URL if present. yearsExperience is the total years of professional experience since the first job after university, as a whole number (internships excluded), or null if it cannot be worked out from the dates. Use an empty string for any other field not in the CV. Never invent data. No markdown, no explanation.";

// Exportado (23/10): la ficha del candidato lo usa para "Editar datos" (la
// Tabla, donde estaba, está congelada). Un solo formulario para alta y edición.
export const CandidateFormModal = ({ position, lang, existing, onSave, onClose, isAdmin = false }) => {
  const isEdit = !!existing;
  const es = lang === "es";
  const isMobile = useIsMobile();
  const [form, setForm] = useState(isEdit
    ? { name:existing.name||"", company:existing.company||"", currentPosition:existing.position||"", city:existing.city||"", salary:existing.salary||"", notice:existing.notice||"", yearsExperience:existing.yearsExperience ?? "", email:existing.email||"", phone:existing.phone||"", linkedin:existing.linkedin||"", status:existing.status||"pending" }
    : { name:"", company:"", currentPosition:"", city:"", salary:"", notice:"", yearsExperience:"", email:"", phone:"", linkedin:"", status:"pending" });
  // Años: número entero o vacío (null en la base)
  const yearsVal = form.yearsExperience === "" ? null : Number(form.yearsExperience);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // CV: solo al AÑADIR. En una ficha que ya existe se sube desde la pestaña CV.
  const [cvFile, setCvFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseMsg, setParseMsg] = useState(null); // { ok, text }
  const [cvFailed, setCvFailed] = useState(null); // candidato creado pero el CV no subió
  const cvInputRef = useRef(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = { width:"100%", boxSizing:"border-box", padding:"9px 12px", border:`1.5px solid ${LINE}`, borderRadius:8, fontSize: 13, outline:"none", fontFamily:"inherit",
    // Móvil: 44 de alto y 16 de letra (por debajo de 16, iOS hace zoom al escribir)
    ...(isMobile ? { minHeight:44, fontSize: 16 } : null) };
  // Móvil: el formulario es una hoja a pantalla completa (encima de la barra
  // inferior), con su cabecera fija y su propio scroll. Escritorio: la tarjeta de siempre.
  const shell = isMobile
    ? { position:"fixed", inset:0, background:WHITE, zIndex:120, display:"flex", flexDirection:"column", paddingTop:"env(safe-area-inset-top)", boxSizing:"border-box" }
    : { ...card, marginBottom:20 };
  const body = isMobile ? { flex:1, overflowY:"auto", padding:"16px 16px calc(24px + env(safe-area-inset-bottom))", WebkitOverflowScrolling:"touch" } : undefined;
  const grid3 = { display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:12 };
  const headerStyle = isMobile
    ? { display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"6px 8px 6px 16px", borderBottom:`1px solid ${ROW_LINE}`, flexShrink:0 }
    : { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 };
  const lbl = { fontSize: 11, color:MUTED, display:"block", marginBottom:3 };

  // Autocompletar: el CV va al proxy de Anthropic (sin `model`: lo fija el
  // proxy) y lo extraído rellena SOLO los campos vacíos. Nada de lo que ya
  // se haya escrito a mano se sobrescribe.
  const autofill = async () => {
    if (!cvFile) return;
    setParsing(true); setParseMsg(null);
    try {
      const data64 = await pdfToBase64(cvFile);
      const res = await aiFetch({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          max_tokens: 800,
          system: CV_EXTRACT_PROMPT,
          messages: [{ role: "user", content: [
            { type: "document", source: { type: "base64", media_type: "application/pdf", data: data64 } },
            { type: "text", text: "Extract the fields from this CV." },
          ]}],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || data?.error || `HTTP ${res.status}`);
      const raw = (data.content || []).map(b => b.text || "").join("");
      const parsed = JSON.parse(raw.replace(/```json|```/g, "").trim());
      const keys = ["name", "company", "currentPosition", "city", "email", "phone", "linkedin"];
      const filledKeys = keys.filter(k => {
        const v = typeof parsed[k] === "string" ? parsed[k].trim() : "";
        return v && !String(form[k] || "").trim();
      });
      const yrs = Number.isInteger(parsed.yearsExperience) && parsed.yearsExperience >= 0 && parsed.yearsExperience < 60 ? parsed.yearsExperience : null;
      if (yrs !== null && form.yearsExperience === "") filledKeys.push("yearsExperience");
      setForm(f => {
        const next = { ...f };
        filledKeys.forEach(k => { if (k === "yearsExperience") { if (f.yearsExperience === "") next.yearsExperience = String(yrs); } else if (!String(f[k] || "").trim()) next[k] = parsed[k].trim(); });
        return next;
      });
      setParseMsg({ ok: true, text: filledKeys.length > 0
        ? (es ? `Rellenados ${filledKeys.length} campos desde el CV. Revísalos antes de guardar.` : `Filled ${filledKeys.length} fields from the CV. Check them before saving.`)
        : (es ? "El CV no aportó datos nuevos: los campos ya estaban rellenos o no aparecen." : "The CV added nothing new: fields were already filled or not found.") });
    } catch (e) {
      console.error("CV autofill error:", e);
      setParseMsg({ ok: false, text: (es ? "No se pudo leer el CV: " : "Couldn't read the CV: ") + (e.message || "error") });
    }
    setParsing(false);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        const { data, error:err } = await supabase.from("candidates").update({
          name: form.name.trim(), company: form.company, current_position: form.currentPosition,
          city: form.city, salary: form.salary, notice: form.notice, email: form.email,
          phone: form.phone, linkedin: form.linkedin, status: form.status, years_experience: yearsVal,
        }).eq("id", String(existing.id)).select("id");
        if (err) throw err;
        if (!data || data.length === 0) throw new Error(es ? "no se guardó (sin permiso)" : "not saved (no permission)");
        onSave({ ...existing, name:form.name.trim(), company:form.company, position:form.currentPosition, city:form.city, salary:form.salary, notice:form.notice, yearsExperience:yearsVal, email:form.email, phone:form.phone, linkedin:form.linkedin, status:form.status });
      } else {
        const uuid = genUUID();
        const qualSections = BLANK_QUAL_SECTIONS.map(s=>({...s,items:s.items.map(it=>({...it,
          a:it.n==="1.1"?form.name:it.n==="1.2"?form.city:it.n==="1.3"?form.salary:it.n==="1.4"?form.notice:""}))}));
        const nc = { id:uuid, name:form.name.trim(), company:form.company, position:form.currentPosition,
          city:form.city, salary:form.salary, notice:form.notice, yearsExperience:yearsVal, email:form.email, phone:form.phone, linkedin:form.linkedin,
          appDate:new Date().toLocaleDateString("es-ES",{day:"2-digit",month:"2-digit",year:"2-digit"}),
          status:"pending", scoring:{technical:0,soft:0,cultural:0,growth:0},
          verdict:"—", strengths:[], weaknesses:[], qualSections, refsData:[], references:0 };
        const saved = await saveCandidateToSupabase(nc, position.id, position.clientId);
        if (saved?.error) throw saved.error;
        // El CV, mismo bucket (privado) y mismo nombre que la pestaña CV de la
        // ficha. En cv_url se guarda la RUTA, no una URL: se firma al leerla.
        if (cvFile) {
          let cvErr = null;
          try {
            const path = await uploadCandidateCv(uuid, cvFile);
            const { data: upd, error: updErr } = await supabase.from("candidates").update({ cv_url: path }).eq("id", uuid).select("id");
            if (updErr || !upd || upd.length === 0) cvErr = updErr || new Error(es ? "no se guardó el enlace" : "link not saved");
            else nc.cvUrl = path;
          } catch (e) { cvErr = e; }
          if (cvErr) {
            // El candidato SÍ está creado: se añade a la lista, pero el
            // formulario se queda abierto diciendo que falta el CV.
            onSave(nc, { keepOpen: true });
            setCvFailed(cvErr.message || String(cvErr));
            setSaving(false);
            return;
          }
        }
        onSave(nc);
      }
    } catch(e) {
      console.error("Save error:", e);
      setError("Error: "+(e.message||JSON.stringify(e)));
    }
    setSaving(false);
  };

  const statusOptions = [
    { value: "pending",   label: es ? "Pendiente"  : "Pending" },
    { value: "approved",  label: es ? "Validado"   : "Validated" },
    { value: "interview", label: es ? "Entrevista" : "Interview" },
    { value: "references",label: es ? "Referencias": "References" },
    { value: "rejected",  label: es ? "Rechazado"  : "Rejected" },
  ];

  const field = (k, l, ph) => (
    <div key={k}><label style={lbl}>{l}</label>
    <input value={form[k]} placeholder={ph || ""} onChange={e=>set(k,e.target.value)} style={inp} onFocus={e=>e.target.style.borderColor=BRAND} onBlur={e=>e.target.style.borderColor={LINE}}/></div>
  );

  if (cvFailed) return (
    <div style={shell}>
      {isMobile && (
        <div style={headerStyle}>
          <h3 style={{ ...text.cardTitle, margin:0 }}>{es?"Añadir candidato":"Add candidate"}</h3>
          <CloseButton onClick={onClose} label={es?"Cerrar":"Close"} />
        </div>
      )}
      <div style={body}>
      <div style={{ fontSize: 13, color:INK, lineHeight:1.6, marginBottom:14 }}>
        {es
          ? <>Candidato <strong>{form.name.trim()}</strong> añadido, pero el CV no se pudo subir ({cvFailed}). Súbelo desde su ficha, pestaña CV.</>
          : <>Candidate <strong>{form.name.trim()}</strong> added, but the CV couldn't be uploaded ({cvFailed}). Upload it from their profile, CV tab.</>}
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <button onClick={onClose} style={{ ...btn.ghost, ...(isMobile ? { width:"100%", minHeight:44 } : null) }}>{es?"Cerrar":"Close"}</button>
      </div>
      </div>
    </div>
  );

  return (
    <div style={shell}>
      <div style={headerStyle}>
        <h3 style={{ ...text.cardTitle, margin:0 }}>{isEdit?(es?"Editar candidato":"Edit candidate"):(es?"Añadir candidato":"Add candidate")}</h3>
        <CloseButton onClick={onClose} label={es?"Cerrar":"Close"} />
      </div>
      <div style={body}>

      {/* CV primero: si se sube, autocompleta los campos de abajo */}
      {!isEdit && (
        <div style={{ background:SURFACE, border:`1px solid ${LINE}`, borderRadius:12, padding:"14px 16px", marginBottom:18 }}>
          <div style={{ fontSize: 11, fontWeight:700, color:MUTED, textTransform:"uppercase", letterSpacing:0.4, marginBottom:8 }}>{es ? "CV (PDF, opcional)" : "CV (PDF, optional)"}</div>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <input ref={cvInputRef} type="file" accept="application/pdf" style={{ display:"none" }}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; if (!f.type.includes("pdf")) { setParseMsg({ ok:false, text: es ? "Solo se admiten PDF." : "Only PDF files are accepted." }); return; } setCvFile(f); setParseMsg(null); }} />
            <button onClick={() => cvInputRef.current?.click()}
              style={{ ...btn.ghost, padding:"7px 14px", ...(isMobile ? { minHeight:44, flexShrink:0 } : null) }}>
              {cvFile ? (es ? "Cambiar archivo" : "Change file") : (es ? "Elegir PDF" : "Choose PDF")}
            </button>
            <span style={{ fontSize: 12, color: cvFile ? TEXT : FAINT, maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", ...(isMobile ? { maxWidth:"none", flex:"1 1 0", minWidth:0 } : null) }} title={cvFile?.name || ""}>
              {cvFile ? cvFile.name : (es ? "Ningún archivo" : "No file chosen")}
            </span>
            <button onClick={autofill} disabled={!cvFile || parsing}
              style={{ ...(!cvFile ? btn.disabled : btn.soft), marginLeft:"auto", display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", cursor: !cvFile ? "not-allowed" : parsing ? "wait" : "pointer",
                ...(isMobile ? { marginLeft:0, width:"100%", minHeight:44, justifyContent:"center" } : null) }}>
              <Sparkles size={14} strokeWidth={1.75} />
              {parsing ? (es ? "Leyendo CV..." : "Reading CV...") : (es ? "Autocompletar con el CV" : "Autofill from CV")}
            </button>
          </div>
          {parseMsg && (
            <div style={{ ...(parseMsg.ok ? { fontSize: 12, color:TEXT } : errorText), marginTop:10 }}>{parseMsg.text}</div>
          )}
          {cvFile && !parseMsg && (
            <div style={{ fontSize: 11, marginTop:8, color:MUTED }}>{es ? "El CV se guarda con el candidato. Autocompletar solo rellena los campos vacíos." : "The CV is saved with the candidate. Autofill only fills empty fields."}</div>
          )}
        </div>
      )}

      <div style={{ ...grid3, marginBottom:12 }}>
        {field("name", es?"Nombre *":"Name *")}
        {field("company", es?"Empresa":"Company")}
        {field("currentPosition", es?"Cargo actual":"Current role")}
      </div>
      <div style={{ ...grid3, marginBottom:12 }}>
        {field("email", "Email")}
        {field("phone", es?"Teléfono":"Phone")}
        {field("linkedin", "LinkedIn", "https://linkedin.com/in/...")}
      </div>
      <div style={{ ...grid3, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr", marginBottom:18 }}>
        {field("city", es?"Ciudad":"City")}
        {field("salary", es?"Salario":"Salary")}
        {field("notice", es?"Preaviso":"Notice")}
        <div><label style={lbl}>{es?"Años de experiencia":"Years of experience"}</label>
        <input value={form.yearsExperience} inputMode="numeric" onChange={e=>set("yearsExperience", e.target.value.replace(/[^0-9]/g, "").slice(0, 2))} style={inp} onFocus={e=>e.target.style.borderColor=BRAND} onBlur={e=>e.target.style.borderColor=LINE}/></div>
      </div>
      {isEdit && isAdmin && (
        <div style={{ marginBottom:18 }}>
          <label style={{fontSize: 11,color:MUTED,display:"block",marginBottom:6,fontWeight:700,textTransform:"uppercase",letterSpacing:0.3}}>{es?"Estado del candidato":"Candidate status"}</label>
          <div style={{ display:"flex", gap:8, ...(isMobile ? { flexDirection:"column" } : null) }}>
            {statusOptions.map(opt => (
              <button key={opt.value} onClick={() => set("status", opt.value)}
                style={{ flex:1, padding:"9px 12px", borderRadius:8, border:`1.5px solid ${form.status===opt.value ? BRAND : LINE}`, background: form.status===opt.value ? BRAND_LIGHT : WHITE, color: form.status===opt.value ? BRAND : TEXT, fontWeight:700, fontSize: 12, cursor:"pointer", transition:"all 0.15s", ...(isMobile ? { minHeight:44, fontSize: 13, textAlign:"left", padding:"0 14px" } : null) }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <div style={{ ...errorBanner, marginBottom:12 }}>{error}</div>}
      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        <button onClick={onClose} style={{ ...btn.ghost, ...(isMobile ? { flex:1, minHeight:44 } : null) }}>{es?"Cancelar":"Cancel"}</button>
        <button onClick={save} disabled={saving||parsing||!form.name.trim()} style={{ ...(!form.name.trim() ? btn.disabled : btn.primary), ...(isMobile ? { flex:2, minHeight:44 } : null) }}>
          {saving?(es?"Guardando...":"Saving..."):(es?"✓ Guardar":"✓ Save")}
        </button>
      </div>
      </div>
    </div>
  );
};

// ─── AÑADIR DESDE LA BASE ────────────────────────────────────────────────────
// Mete en la posición a alguien que ya está en `people` sin reescribir sus
// datos: se copian de su contacto / cuenta del portal / otra candidatura
// (lib/personCandidate.js, el mismo camino que "Pasar a candidato" del
// Mapping) y la candidatura queda enlazada por person_id a su ficha de
// Database. Solo admin. Mismo marco que el formulario de "Añadir candidato".
const AddFromDatabase = ({ position, lang, onAdded, onOpen, onClose }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // null = sin buscar todavía
  const [searching, setSearching] = useState(false);
  const [chosen, setChosen] = useState(null);   // datos de la persona elegida
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);         // { ok, text }
  const [added, setAdded] = useState(null);     // candidato recién creado

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    let alive = true;
    setSearching(true);
    const tmo = setTimeout(async () => {
      const { data: ppl, error } = await supabase.from("people").select("id, full_name").ilike("full_name", `%${term}%`).order("full_name").limit(10);
      if (!alive) return;
      if (error) { setResults([]); setSearching(false); setMsg({ ok: false, text: (es ? "No se pudo buscar: " : "Search failed: ") + error.message }); return; }
      const ids = (ppl || []).map(p => p.id);
      // Empresa y puesto del contacto enlazado, para distinguir homónimos; y
      // quién está ya en esta posición, para marcarlo antes de elegir.
      const [{ data: cts }, { data: here }] = await Promise.all([
        ids.length ? supabase.from("contacts").select("person_id, company, position, city").in("person_id", ids) : Promise.resolve({ data: [] }),
        ids.length ? supabase.from("candidates").select("person_id").eq("position_id", position.id).in("person_id", ids) : Promise.resolve({ data: [] }),
      ]);
      if (!alive) return;
      const meta = {};
      (cts || []).forEach(c => { if (!meta[c.person_id] || (!meta[c.person_id].company && c.company)) meta[c.person_id] = c; });
      const inPos = new Set((here || []).map(r => String(r.person_id)));
      setResults((ppl || []).map(p => ({ ...p, meta: meta[p.id] || null, inPosition: inPos.has(String(p.id)) })));
      setSearching(false);
    }, 250);
    return () => { alive = false; clearTimeout(tmo); };
  }, [q, position.id, es]);

  const choose = async (p) => {
    setMsg(null); setLoading(true);
    const data = await loadPersonData(p.id);
    setLoading(false);
    setChosen(data);
  };

  const add = async () => {
    if (!chosen) return;
    setSaving(true); setMsg(null);
    const dup = await findExistingCandidacy(position.id, chosen);
    if (dup) {
      setSaving(false);
      setMsg({ ok: false, text: es ? `${dup.name || chosen.name} ya es candidato de esta posición${dup.person_id ? "" : " (mismo email)"}.` : `${dup.name || chosen.name} is already a candidate for this position${dup.person_id ? "" : " (same email)"}.` });
      return;
    }
    const { nc, error, warn, photoUrl } = await createCandidateFromPerson(chosen, position);
    setSaving(false);
    if (error) { setMsg({ ok: false, text: (es ? "No se pudo crear la candidatura: " : "Couldn't create the candidate: ") + error.message }); return; }
    onAdded(nc, photoUrl);
    setAdded(nc);
    setChosen(null); setQ(""); setResults(null);
    if (warn) setMsg({ ok: false, text: es ? "Creado, pero no se pudo enlazar a su ficha de Database (person_id)." : "Created, but couldn't link it to the Database profile (person_id)." });
  };

  const shell = isMobile
    ? { position: "fixed", inset: 0, background: WHITE, zIndex: 120, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" }
    : { ...card, marginBottom: 20 };
  const body = isMobile ? { flex: 1, overflowY: "auto", padding: "16px 16px calc(24px + env(safe-area-inset-bottom))", WebkitOverflowScrolling: "touch" } : undefined;
  const headerStyle = isMobile
    ? { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 8px 6px 16px", borderBottom: `1px solid ${ROW_LINE}`, flexShrink: 0 }
    : { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 };
  const bM = isMobile ? { minHeight: 44 } : null;
  const metaLine = (m) => [m?.position && m?.company ? `${m.position} · ${m.company}` : (m?.position || m?.company), m?.city].filter(Boolean).join(" · ");
  const lbl = text.label;

  return (
    <div style={shell}>
      <div style={headerStyle}>
        <h3 style={{ ...text.cardTitle, margin: 0 }}>{es ? "Añadir desde la base" : "Add from database"}</h3>
        <CloseButton onClick={onClose} label={es ? "Cerrar" : "Close"} />
      </div>
      <div style={body}>

      {added && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
          <span style={{ fontSize: 13, color: INK, flex: "1 1 200px" }}>
            {es ? <><strong>{added.name}</strong> está en el Tablero, en Pendiente.</> : <><strong>{added.name}</strong> is on the Board, in Pending.</>}
          </span>
          <button onClick={() => onOpen(added)} style={{ ...btn.primary, ...bM }}>{es ? "Abrir ficha" : "Open profile"}</button>
        </div>
      )}

      {chosen ? (
        <div>
          <div style={{ ...lbl, marginBottom: 8 }}>{es ? "Se añadirá con estos datos" : "Will be added with this data"}</div>
          <div style={{ border: `1.5px solid ${LINE}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>{chosen.name}</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>{metaLine({ position: chosen.position, company: chosen.company, city: chosen.city }) || (es ? "Sin empresa ni puesto" : "No company or role")}</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10, marginTop: 12 }}>
              {[["Email", chosen.email], [es ? "Teléfono" : "Phone", chosen.phone], ["LinkedIn", chosen.linkedin]].map(([l, v]) => (
                <div key={l} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 13, color: v ? TEXT : FAINT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v || ""}>{v || "—"}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 12 }}>
              {es ? "Los datos se copian de su ficha y no se cambian. Entra en Pendiente." : "Data is copied from their profile and not changed. Starts in Pending."}
            </div>
          </div>
          {msg && <div style={{ ...(msg.ok ? { fontSize: 12, color: TEXT } : errorText), marginBottom: 12 }}>{msg.text}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => { setChosen(null); setMsg(null); }} style={{ ...btn.ghost, ...bM, ...(isMobile ? { flex: 1 } : null) }}>{es ? "Volver" : "Back"}</button>
            <button onClick={add} disabled={saving} style={{ ...btn.primary, ...bM, cursor: saving ? "wait" : "pointer", ...(isMobile ? { flex: 2 } : null) }}>
              {saving ? (es ? "Añadiendo..." : "Adding...") : (es ? `Añadir a ${position.title || "la posición"}` : `Add to ${position.title || "the position"}`)}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", display: "flex" }}><Icon name="search" size={15} color={MUTED} /></div>
            <input autoFocus value={q} onChange={e => { setQ(e.target.value); setMsg(null); }} placeholder={es ? "Buscar por nombre..." : "Search by name..."}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 36px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, outline: "none", fontFamily: "inherit", ...(isMobile ? { minHeight: 44 } : null) }}
              onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
          </div>
          {msg && <div style={{ ...(msg.ok ? { fontSize: 12, color: TEXT } : errorText), marginBottom: 12 }}>{msg.text}</div>}
          {results === null ? (
            <div style={{ fontSize: 12, color: MUTED }}>{es ? "Escribe al menos dos letras del nombre." : "Type at least two letters of the name."}</div>
          ) : searching ? (
            <div style={{ fontSize: 12, color: MUTED }}>{es ? "Buscando..." : "Searching..."}</div>
          ) : results.length === 0 ? (
            <div style={{ fontSize: 12, color: MUTED }}>{es ? "Nadie con ese nombre en la base. Si es nuevo, usa \"Añadir candidato\"." : "No one with that name in the database. If they're new, use \"Add candidate\"."}</div>
          ) : (
            <div style={{ border: `1.5px solid ${LINE}`, borderRadius: 12, overflow: "hidden" }}>
              {results.map((p, i) => (
                <button key={p.id} onClick={() => !p.inPosition && choose(p)} disabled={p.inPosition || loading}
                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: WHITE, border: "none", borderTop: i ? `1px solid ${ROW_LINE}` : "none", padding: "10px 14px", fontFamily: "inherit", cursor: p.inPosition ? "default" : loading ? "wait" : "pointer", minHeight: isMobile ? 52 : undefined }}
                  onMouseEnter={e => { if (!p.inPosition) e.currentTarget.style.background = SURFACE; }} onMouseLeave={e => { e.currentTarget.style.background = WHITE; }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: p.inPosition ? MUTED : INK }}>{p.full_name}</div>
                    <div style={{ fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{metaLine(p.meta) || (es ? "Sin contacto enlazado" : "No linked contact")}</div>
                  </div>
                  {p.inPosition && <span style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>{es ? "Ya en esta posición" : "Already in this position"}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};



// Icono de la vista Tablero. Dibujado aquí y no importado de lucide-react:
// si el nombre no existe en la versión instalada, el build de Vercel falla.
const BoardIcon = ({ size = 17, strokeWidth = 1.75, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M8 7v7" /><path d="M12 7v4" /><path d="M16 7v9" />
  </svg>
);

// Vistas del pipeline de candidatos. Tabla y Wall CONGELADAS (20/10, para
// simplificar): el código sigue y se reactivan añadiéndolas aquí ("table",
// "wall"). Con una sola vista no se enseña el selector.
const CANDIDATE_VIEWS = ["board"];

const CandidatesView = ({ t, lang, position, setView, setSelectedCandidate, backView = "open", candidateStatuses = {}, setCandidateStatuses = () => {}, setInitialCandidateTab = () => {}, setBackView = () => {}, seenReports = new Set(), photoUrls = {}, isAdmin = false, setOpenPositionsList = () => {}, companyLogoUrl = null, companyName = "", activeClientId = null, calendlyLink = "", chatUnread = 0, setPhotoUrls = () => {}, onOpenChat = () => {} }) => {
  const [search, setSearch] = useState("");
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [showAddFromDb, setShowAddFromDb] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [confirmIdx, setConfirmIdx] = useState(null); // null | { id, step: "validate" | "email" }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [clientCultureDoc, setClientCultureDoc] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [starred, setStarred] = useState({});      // {candidateId: bool}
  const [onlyStarred, setOnlyStarred] = useState(false);
  // Vista Wall: el equivalente digital de repartir fotos por una sala. Lo que
  // aporta no es ver caras grandes, es poder MOVERLAS y compararlas. El orden
  // es deliberadamente efímero: se pierde al salir, como en la sala real.
  const [wall, setWall] = useState(false);
  const [wallOrder, setWallOrder] = useState([]);
  // Wall tiene DOS MODOS excluyentes, no una preferencia visual:
  //   "faces" → caras grandes, sin números. Se juzga con la intuición.
  //   "data"  → sin caras, con la evidencia. Se juzga con los datos.
  // No poder verlos a la vez es lo que separa los dos juicios en vez de
  // mezclarlos, que es como la apariencia contamina el análisis sin notarlo.
  const [wallMode, setWallMode] = useState("faces");
  const [dragId, setDragId] = useState(null);
  const [dimTip, setDimTip] = useState(null);
  // Qué puntuación pinta el radar de las tarjetas: la de Rutment o la de la
  // empresa. Va en la barra y no por tarjeta: con diez candidatos, diez
  // conmutadores serían diez decisiones para una sola pregunta.
  const [wallScore, setWallScore] = useState("qual");
  // ── Tablero (kanban) ──────────────────────────────────────────────────
  // Tercera vista: columnas por fase y arrastrar para mover. A diferencia
  // del Wall, aquí arrastrar SÍ cambia el estado, así que cada movimiento
  // pasa por las mismas reglas y confirmaciones que los botones de la tabla.
  // El Tablero es la vista por defecto del pipeline.
  const [board, setBoard] = useState(true);
  // Cabecera fija de la posición (título y pestañas): su alto va a la variable
  // CSS --pos-head, para que lo que también se queda fijo debajo (el formulario
  // de Evaluación, la guía) empiece justo después y no quede tapado
  const [tabHelp, setTabHelp] = useState(false); // ayuda ("?") de la pestaña abierta
  const posHeadRef = useRef(null);
  const [posHeadH, setPosHeadH] = useState(0);
  useEffect(() => {
    const el = posHeadRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setPosHeadH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Pestaña de la posición. Se recuerda para que, al volver de la ficha de
  // un candidato (por ejemplo tras evaluarlo), se aterrice donde se estaba.
  // Solo para la MISMA posición: abrir otra empieza siempre en Candidatos.
  const [posTab, setPosTab] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("rutment_position_tab") || "null");
      if (saved && String(saved.id) === String(position?.id) && ["mapping", "candidates", "evaluation", "documents"].includes(saved.tab)) return saved.tab;
    } catch (e) {}
    return "candidates";
  });
  useEffect(() => {
    try { localStorage.setItem("rutment_position_tab", JSON.stringify({ id: position?.id, tab: posTab })); } catch (e) {}
  }, [posTab, position?.id]);
  // Llegada desde «Lo que toca» de Home (31/10): el número rojo de Pendientes
  // echa dos ondas para señalar dónde está lo que hay que hacer. Aviso de un
  // solo uso (sessionStorage), solo para esta posición.
  const [pulsePending] = useState(() => {
    try {
      const f = JSON.parse(sessionStorage.getItem("rutment_focus") || "null");
      sessionStorage.removeItem("rutment_focus");
      return !!f && f.what === "pending" && String(f.id) === String(position?.id);
    } catch (e) { return false; }
  });
  const [boardDragId, setBoardDragId] = useState(null);
  const [boardOver, setBoardOver] = useState(null);
  const [boardConfirm, setBoardConfirm] = useState(null); // null | { id, step: "validate" | "reject" }
  const [boardMsg, setBoardMsg] = useState("");
  // Tarjeta sobre la que se va a soltar al reordenar dentro de una columna
  const [boardOverCard, setBoardOverCard] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  // Móvil: el Tablero enseña UNA columna cada vez (se elige arriba) y cada
  // tarjeta lleva "Mover a…" y ↑/↓, porque arrastrar no existe en táctil.
  const isMobile = useIsMobile();
  const [boardCol, setBoardCol] = useState("pending");
  const [showRejected, setShowRejected] = useState(false); // tablero: columna Descartados desplegada (31/10)
  useEffect(() => {
    const cid = position?.clientId || activeClientId;
    if (!cid) return;
    supabase.from("culture_docs").select("generated_doc").eq("client_id", cid).maybeSingle()
      .then(({ data }) => { if (data?.generated_doc) setClientCultureDoc(data.generated_doc); });
  }, [position?.clientId, activeClientId]);
  useEffect(() => {
    if (!position) return;
    const init = {};
    (position.candidates || []).forEach(c => { if (c.starred) init[c.id] = true; });
    setStarred(init);
  }, [position?.id]);
  if (!position) return null;

  const getStatus = (c) => candidateStatuses[c.id] || c.status;

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  // La fase REAL no es el campo `status`: en la base casi todos están como
  // "approved" y la etiqueta se deriva de si tienen referencias o evaluación,
  // igual que hace StatusBadge. Ordenar por el campo crudo mezclaba filas con
  // etiquetas distintas, y por eso salían References arriba y abajo.
  const phaseOf = (c) => {
    const st = candidateStatuses[c.id] || c.status;
    if (st === "rejected") return "rejected";
    if (st === "pending") return "pending";
    if (st === "references") return "references";
    if (st === "interview") return "interview";
    if (st === "approved") {
      if (c.references >= 1 || (c.refsData || []).length > 0) return "references";
      if ((c.evalManagers || []).length > 0 && c.evalUpdatedAt) return "interview";
      return "validated";
    }
    return "pending";
  };

  // Orden por defecto: primero lo que requiere acción (Pending), después lo
  // más avanzado, y lo cerrado al fondo. Al pulsar una cabecera, manda esa.
  const statusOrder = { pending: 0, references: 1, interview: 2, validated: 3, rejected: 4 };

  // Confirmaciones: ConfirmBar de shared.jsx (contenedor neutro, un solo
  // botón con color, Cancelar en gris). Validar tiene una tercera opción,
  // "sin email", que va en gris como hijo de la barra.
  const noEmailBtn = (onClick) => (
    <button type="button" onClick={onClick} style={{ ...btn.ghost, ...(isMobile ? { flex: "1 1 100%", minHeight: 44 } : { padding: "6px 12px", fontSize: 12 }) }}>
      {lang === "es" ? "Confirmar sin email" : "Confirm without email"}
    </button>
  );

  const toggleStar = async (e, id) => {
    e.stopPropagation();
    const next = !starred[id];
    setStarred(prev => ({ ...prev, [id]: next }));
    const { data, error } = await supabase.from("candidates").update({ starred: next }).eq("id", id).select();
    // RLS bloqueado devuelve 200 con 0 filas y sin error: hay que comprobarlo
    if (error || !data || data.length === 0) setStarred(prev => ({ ...prev, [id]: !next }));
  };

  // Preguntas de evaluación de esta posición (o las 8 por defecto)
  const evalIds = evalItemIds(lang, position.evalQuestions);

  const filtered = (() => {
    let arr = position.candidates.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.company.toLowerCase().includes(search.toLowerCase()));
    if (onlyStarred) arr = arr.filter(c => starred[c.id]);
    const _getEvalAvg = (c) => evalAvg5(c.evalManagers, c.evalScores, evalIds) ?? -1;
    if (sortKey) arr = [...arr].sort((a, b) => {
      let va, vb;
      if (sortKey === "name") { va = a.name.toLowerCase(); vb = b.name.toLowerCase(); }
      else if (sortKey === "company") { va = a.company.toLowerCase(); vb = b.company.toLowerCase(); }
      else if (sortKey === "appDate") { va = a.appDate.split("/").reverse().join(""); vb = b.appDate.split("/").reverse().join(""); }
      else if (sortKey === "status") { va = statusOrder[phaseOf(a)] ?? 9; vb = statusOrder[phaseOf(b)] ?? 9; }
      else if (sortKey === "rut") { va = a.scoring ? Object.values(a.scoring).reduce((s,v)=>s+v,0)/4 : 0; vb = b.scoring ? Object.values(b.scoring).reduce((s,v)=>s+v,0)/4 : 0; }
      else if (sortKey === "eval") { va = _getEvalAvg(a); vb = _getEvalAvg(b); }
      else return 0;
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    // Sin orden manual elegido: se aplica el del pipeline, y a igualdad de
    // fase, el más reciente primero.
    else arr = [...arr].sort((a, b) => {
      const sa = statusOrder[phaseOf(a)] ?? 9;
      const sb = statusOrder[phaseOf(b)] ?? 9;
      if (sa !== sb) return sa - sb;
      const da = (a.appDate || "").split("/").reverse().join("");
      const db = (b.appDate || "").split("/").reverse().join("");
      return db.localeCompare(da);
    });
    return arr;
  })();


  const sendValidationEmail = async (c) => {
    try {
      const candidateEmail = c.email || "";
      const res = await authFetch("/api/send-email", {
        body: JSON.stringify({
          type: "candidate_validated",
          candidateId: c.id,
          candidate: { name: c.name, email: candidateEmail, position: c.position, company: c.company, linkedin: c.linkedin },
          position: { title: position?.title || "" },
          interviewers: [],
          calendlyLink: calendlyLink,
          lang,
        })
      });
      // Decir si el correo salió (23/10): antes se daba por enviado siempre
      const out = await res.json().catch(() => ({}));
      if (!res.ok) setBoardMsg(lang === "es" ? `No se pudo enviar el correo a ${c.name}.` : `The email to ${c.name} could not be sent.`);
      else if (out.candidateEmailed === false) setBoardMsg(lang === "es" ? `${c.name} no tiene email en su ficha: no se le ha enviado nada.` : `${c.name} has no email on file: nothing was sent to them.`);
    } catch(e) { console.error("Email send error:", e); setBoardMsg(lang === "es" ? "No se pudo enviar el correo." : "The email could not be sent."); }
  };

  // Deshace el cambio optimista de estado y lo dice (el aviso sale encima de
  // la tabla y del tablero).
  const revertStatus = (c, before) => {
    setCandidateStatuses(p => { const n = { ...p }; if (before === undefined) delete n[c.id]; else n[c.id] = before; return n; });
    setBoardMsg(lang === "es" ? `No se pudo guardar el cambio de ${c.name}. Inténtalo de nuevo.` : `Couldn't save the change for ${c.name}. Please try again.`);
  };

  const handleConfirmValidate = async (c, sendEmail) => {
    const before = candidateStatuses[c.id];
    setCandidateStatuses(prev => ({ ...prev, [c.id]: "approved" }));
    setConfirmIdx(null);
    // RLS bloqueado devuelve 200 con 0 filas: si no se guardó, se deshace y
    // no se avisa a nadie ni se manda el correo.
    const { data, error } = await supabase.from("candidates").update({ status: "approved" }).eq("id", c.id).select();
    if (error || !data || data.length === 0) {
      revertStatus(c, before);
      return;
    }
    supabase.from("admin_notifications").insert({
      type: "candidate_validated",
      message: `${c.name} validated for ${position?.title || ""}`,
      candidate_name: c.name,
      position_title: position?.title || "",
    });
    if (sendEmail) await sendValidationEmail(c);
  };

  // Rechazo: lo mismo que hace el botón de la tabla, en una función para
  // poder usarlo también desde el tablero.
  const rejectCandidate = async (c) => {
    const before = candidateStatuses[c.id];
    setCandidateStatuses(prev => ({ ...prev, [c.id]: "rejected" }));
    const { data, error } = await supabase.from("candidates").update({ status: "rejected" }).eq("id", c.id).select();
    if (error || !data || data.length === 0) {
      revertStatus(c, before);
      return;
    }
    supabase.from("admin_notifications").insert({ type: "candidate_rejected", message: `${c.name} rejected for ${position?.title || ""}`, candidate_name: c.name, position_title: position?.title || "" });
  };

  // Borrar un candidato (solo desde la vista Tabla, a propósito: en el
  // Tablero se arrastra, y una papelera junto a lo que se arrastra se pulsa
  // sin querer). Comprueba
  // que el delete devolvió la fila (RLS bloqueado = 200 con 0 filas).
  const deleteCandidate = async (c) => {
    setBoardMsg("");
    const { data, error } = await supabase.from("candidates").delete().eq("id", c.id).select("id");
    if (error || !data || data.length === 0) {
      setBoardMsg(lang === "es" ? `No se pudo eliminar a ${c.name}.${error ? " " + error.message : ""}` : `Couldn't delete ${c.name}.${error ? " " + error.message : ""}`);
      return;
    }
    setOpenPositionsList(prev => prev.map(p => p.id === position.id ? { ...p, candidates: (p.candidates || []).filter(x => String(x.id) !== String(c.id)) } : p));
  };

  // Columnas en el ORDEN DEL PROCESO, no en el de la tabla (que pone primero
  // lo que pide acción). Rechazados aparte, al final y en gris.
  const BOARD_COLS = [
    { key: "pending",    es: "Pendientes",  en: "Pending" },
    { key: "validated",  es: "Validados",   en: "Validated" },
    { key: "interview",  es: "Entrevista",  en: "Interview" },
    { key: "references", es: "Referencias", en: "References" },
    { key: "rejected",   es: "Descartados", en: "Rejected" },
  ];

  // "Validado" es el estado `approved`, y su fase se CALCULA: con evaluación
  // sale como Interview y con referencias como References. Soltar ahí a
  // alguien que ya tiene esos datos lo haría saltar solo a otra columna.
  const hasDerivedData = (c) =>
    c.references >= 1 || (c.refsData || []).length > 0 || ((c.evalManagers || []).length > 0 && c.evalUpdatedAt);

  // Devuelve null si se puede soltar, o el motivo si no. Mismas reglas que
  // la tabla y la ficha: el cliente solo valida o descarta pendientes;
  // Interview, References y volver atrás son cosa del admin.
  const boardBlock = (c, to) => {
    const from = phaseOf(c);
    if (from === to) return null;
    if (to === "validated" && hasDerivedData(c))
      return lang === "es" ? "Ya tiene evaluación o referencias: volvería a su fase sola" : "Already has evaluation or references: it would jump back";
    // Desde Pendientes solo se valida o se descarta, también el admin: validar
    // pasa por la confirmación con el correo al candidato.
    if (from === "pending" && to !== "validated" && to !== "rejected")
      return lang === "es" ? "Desde Pendientes solo se puede validar o descartar" : "From Pending you can only validate or reject";
    if (isAdmin) return null;
    if (from !== "pending")
      return lang === "es" ? "Solo los candidatos pendientes se pueden mover" : "Only pending candidates can be moved";
    if (to !== "validated" && to !== "rejected")
      return lang === "es" ? "Esta fase la gestiona Rutment" : "This stage is managed by Rutment";
    return null;
  };

  // Mover sin confirmación: solo pasos que no destruyen nada ni avisan a
  // nadie (los del admin entre fases). Comprueba que de verdad se guardó:
  // un update bloqueado por RLS devuelve 200 con 0 filas.
  const BOARD_STATUS = { pending: "pending", validated: "approved", interview: "interview", references: "references" };
  const boardMove = async (c, to) => {
    const ns = BOARD_STATUS[to];
    const prev = candidateStatuses[c.id];
    setCandidateStatuses(p => ({ ...p, [c.id]: ns }));
    const { data, error } = await supabase.from("candidates").update({ status: ns }).eq("id", c.id).select();
    if (error || !data || data.length === 0) {
      setCandidateStatuses(p => { const n = { ...p }; if (prev === undefined) delete n[c.id]; else n[c.id] = prev; return n; });
      setBoardMsg(lang === "es" ? `No se pudo mover a ${c.name}. Inténtalo de nuevo.` : `Couldn't move ${c.name}. Please try again.`);
    }
  };

  // Orden dentro de una columna: el manual (board_order) primero; los que no
  // tienen, detrás y en el orden de siempre.
  const sortBoard = (list) => list
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      const ao = a.c.boardOrder, bo = b.c.boardOrder;
      if (ao == null && bo == null) return a.i - b.i;
      if (ao == null) return 1;
      if (bo == null) return -1;
      return ao - bo || a.i - b.i;
    })
    .map(x => x.c);

  // Mover una tarjeta dentro de su columna: delante de `before`, o al final.
  // Se numera toda la columna y se guarda; si algo no se guarda (RLS = 0
  // filas), se deshace y se avisa.
  const boardReorder = async (cards, dragged, before) => {
    if (!dragged || (before && String(before.id) === String(dragged.id))) return;
    const ids = cards.map(x => String(x.id)).filter(id => id !== String(dragged.id));
    const at = before ? ids.indexOf(String(before.id)) : ids.length;
    ids.splice(at < 0 ? ids.length : at, 0, String(dragged.id));
    const order = Object.fromEntries(ids.map((id, i) => [id, i]));
    const prev = Object.fromEntries(cards.map(x => [String(x.id), x.boardOrder ?? null]));
    const apply = (map) => setOpenPositionsList(pl => pl.map(p => p.id !== position.id ? p : {
      ...p, candidates: (p.candidates || []).map(x => String(x.id) in map ? { ...x, boardOrder: map[String(x.id)] } : x),
    }));
    apply(order);
    const changed = ids.filter(id => prev[id] !== order[id]);
    const results = await Promise.all(changed.map(id =>
      supabase.from("candidates").update({ board_order: order[id] }).eq("id", id).select("id")));
    if (results.some(r => r.error || !r.data || r.data.length === 0)) {
      apply(prev);
      setBoardMsg(lang === "es" ? "No se pudo guardar el nuevo orden. Inténtalo de nuevo." : "Couldn't save the new order. Please try again.");
    }
  };

  const boardDrop = (c, to) => {
    setBoardMsg("");
    if (!c || phaseOf(c) === to || boardBlock(c, to)) return;
    const from = phaseOf(c);
    // Validar y descartar desde pendientes: la MISMA confirmación que los
    // botones, con la opción del correo al validar. Nunca de un solo gesto.
    if (from === "pending" && (to === "validated" || to === "rejected")) {
      setBoardConfirm({ id: c.id, step: to === "validated" ? "validate" : "reject" });
      return;
    }
    if (to === "rejected") { setBoardConfirm({ id: c.id, step: "reject" }); return; }
    boardMove(c, to);
  };

  // Cambiar de pestaña: la cabecera fija está en su sitio natural arriba del
  // todo, así que el contenido nuevo empieza arriba; si no, quedaba tapado
  // por la cabecera ("Entrevista a pantalla completa" no se veía, 24/10)
  const selectPosTab = (v) => {
    setPosTab(v);
    const mainEl = document.querySelector("main");
    if (mainEl) mainEl.scrollTop = 0;
  };

  // Abrir la ficha de un candidato (mismo destino que la fila de la tabla)
  const openCandidate = (c) => { setInitialCandidateTab("scoring"); setBackView("candidates"); setSelectedCandidate(c); setView("candidateDetail"); };

  return (
    <div style={{ padding: isMobile ? 16 : 32, "--pos-head": `${posHeadH}px` }}>
      {/* Cabecera fija: título y pestañas se quedan arriba al bajar, para
          cambiar de pestaña sin volver arriba. Ocupa todo el ancho con el
          fondo de la app; en el móvil, justo debajo de la barra superior. */}
      <div ref={posHeadRef} style={{ position: "sticky", top: isMobile ? `calc(${MOBILE_TOP}px + env(safe-area-inset-top))` : 0, zIndex: 30, background: BG,
        margin: isMobile ? "-16px -16px 0" : "-32px -32px 0", padding: isMobile ? "16px 16px 0" : "32px 32px 0" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 12, marginBottom: 6 }}>
        {/* backView se reutiliza al abrir una ficha (vale "candidates"), y
            volver a "candidates" desde aquí no hacía nada: en ese caso, a Posiciones. */}
        <BackButton onClick={() => setView(["candidates", "candidateDetail"].includes(backView) ? "positions" : backView)} ariaLabel={lang === "es" ? "Volver" : "Back"} />
        <h1 style={{ ...pageTitle(isMobile), ...(isMobile ? { lineHeight: 1.25, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : null) }} title={position.title}>{position.title}</h1>
        {!isMobile && <span style={chip(false)}>{position.candidates.length} {t.candidates}</span>}
        {/* El "?" explica la pestaña abierta (Mapping, Candidatos, Evaluación o Documentos) */}
        <HelpButton onClick={() => setTabHelp(true)} label={lang === "es" ? "Cómo funciona esta pestaña" : "How this tab works"} />

      </div>

      {/* Barra de la posición: pestañas a la izquierda y chat con el recruiter
          a la derecha, a la misma altura. Las pestañas usan el conmutador del
          sistema (caja gris, activo en blanco con BRAND, peso fijo). Las
          métricas de la posición viven en la pantalla de Posiciones. */}
      <div style={isMobile
        ? { margin: "12px 0 0", paddingBottom: 14, borderBottom: `1px solid ${LINE}` }
        : { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", margin: "16px 0 0", paddingBottom: 16, borderBottom: `1px solid ${LINE}` }}>
        {/* Móvil: el conmutador ocupa todo el ancho y se desliza en horizontal */}
        <div style={isMobile
          ? { ...segWrap, width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", boxSizing: "border-box", alignItems: "center" }
          : { ...segWrap, display: "inline-flex", boxSizing: "border-box", alignItems: "center" }}>
          {[["mapping", Network, "Mapping"],
            ["candidates", Users, lang === "es" ? "Candidatos" : "Candidates"],
            ["evaluation", ClipboardList, lang === "es" ? "Evaluación" : "Evaluation"],
            ["documents", FileText, "Brief"]].map(([v, Ico, label]) => {
            const active = posTab === v;
            return (
              <button key={v} onClick={() => selectPosTab(v)}
                style={{ ...segBtn(active), padding: "0 18px", height: 34, display: "inline-flex", alignItems: "center", gap: 8, transition: "all 0.12s",
                  ...(isMobile ? { height: 40, padding: "0 8px", fontSize: 12, whiteSpace: "nowrap", flex: "1 1 auto", justifyContent: "center" } : null) }}>
                {/* Móvil: sin icono, para que las cuatro pestañas quepan sin desplazar (31/10) */}
                {!isMobile && <Ico size={16} strokeWidth={1.75} color={active ? BRAND : MUTED} />}
                {label}
              </button>
            );
          })}
        </div>

        {/* El chat con el recruiter se abre desde el menú (31/10): aquí era un tercer acceso al mismo chat */}
      </div>

      </div>
      <div style={{ height: isMobile ? 16 : 22 }} />
      {tabHelp && <OnboardingModal help={posTab} lang={lang} onClose={() => setTabHelp(false)} />}

      {/* Mapping: la long list anónima previa al contacto (paso previo a Candidatos) */}
      {posTab === "mapping" && (
        <MappingTab position={position} lang={lang} isAdmin={isAdmin} companyName={companyName} setOpenPositionsList={setOpenPositionsList} setPhotoUrls={setPhotoUrls} />
      )}

      {posTab === "evaluation" && (
        <PositionEvaluationTab position={position} lang={lang} photoUrls={photoUrls} setOpenPositionsList={setOpenPositionsList}
          onOpenCandidate={(c) => { setInitialCandidateTab("evaluation"); setBackView("candidates"); setSelectedCandidate(c); setView("candidateDetail"); }} />
      )}

      {posTab === "documents" && (
        <DocumentsBox lang={lang} jobDescription={position.jobDescription || ""} cultureDoc={position.cultureDoc || ""} positionId={position.id} onCultureDocSave={doc => setOpenPositionsList(prev => prev.map(p => p.id === position.id ? { ...p, cultureDoc: doc } : p))} isAdmin={isAdmin} positionTitle={position.title} companyLogoUrl={companyLogoUrl} companyName={companyName} clientCultureDoc={clientCultureDoc} onOpenCulture={() => setView("documents")} />
      )}

      {posTab === "candidates" && (<>
      {showAddCandidate && isAdmin && <CandidateFormModal position={position} lang={lang} existing={null} isAdmin={isAdmin} onSave={(nc, opts)=>{ setOpenPositionsList(prev=>prev.map(p=>p.id===position.id?{...p,candidates:[nc,...(p.candidates||[])]}:p)); if (!opts?.keepOpen) setShowAddCandidate(false); }} onClose={()=>setShowAddCandidate(false)} />}
      {showAddFromDb && isAdmin && <AddFromDatabase position={position} lang={lang} onAdded={(nc, photoUrl) => { if (photoUrl) setPhotoUrls(pu => ({ ...pu, [nc.id]: photoUrl })); setOpenPositionsList(prev => prev.map(p => p.id === position.id ? { ...p, candidates: [nc, ...(p.candidates || [])] } : p)); }} onOpen={nc => { setShowAddFromDb(false); openCandidate(nc); }} onClose={() => setShowAddFromDb(false)} />}
      {editingCandidate && isAdmin && <CandidateFormModal position={position} lang={lang} existing={editingCandidate} isAdmin={isAdmin} onSave={(upd)=>{ setOpenPositionsList(prev=>prev.map(p=>p.id===position.id?{...p,candidates:p.candidates.map(c=>String(c.id)===String(upd.id)?upd:c)}:p)); if (upd.status !== editingCandidate.status) setCandidateStatuses(prev=>({...prev,[upd.id]:upd.status})); setEditingCandidate(null); }} onClose={()=>setEditingCandidate(null)} />}

      {/* Toolbar: buscador, destacados y acceso directo a evaluación.
          Un solo objeto de estilo para los tres: mismo alto, misma fuente,
          mismo peso. Antes cada uno tenía el suyo y se notaba. */}
      {(() => { const _tb = { height: 38, boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontWeight: 500, fontFamily: "inherit", color: TEXT, background: WHITE, outline: "none" };
      // Móvil: buscador a todo el ancho (16 px: iOS no hace zoom), debajo
      // destacados + modo del Wall + vista, y Añadir candidato como única
      // acción principal, a todo el ancho.
      if (isMobile) {
        const _m = { ..._tb, height: 44 };
        const seg = { ...segWrap, display: "inline-flex", height: 44, boxSizing: "border-box", alignItems: "center" };
        const nStar = Object.values(starred).filter(Boolean).length;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", display: "flex" }}><Icon name="search" size={15} color={MUTED} /></div>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.search + "..."} style={{ ..._m, width: "100%", paddingLeft: 36, paddingRight: 12, fontSize: 16 }} onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={() => setOnlyStarred(v => !v)}
                style={{ ..._m, background: onlyStarred ? BRAND_LIGHT : WHITE, borderColor: onlyStarred ? BRAND : LINE, padding: "0 12px", color: onlyStarred ? BRAND : TEXT, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <span style={{ color: STAR, fontSize: 13, lineHeight: 1 }}>★</span>
                {lang === "es" ? "Destacados" : "Starred"}
                {nStar > 0 && ` (${nStar})`}
              </button>
              {wall && (
                <div style={seg}>
                  {[["faces", lang === "es" ? "Caras" : "Faces"], ["data", lang === "es" ? "Datos" : "Data"]].map(([v, label]) => (
                    <button key={v} onClick={() => setWallMode(v)}
                      style={{ ...segBtn(wallMode === v), padding: "0 12px", height: 36 }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {CANDIDATE_VIEWS.length > 1 && <div style={{ ...seg, marginLeft: "auto" }}>
                {[["board", BoardIcon, lang === "es" ? "Tablero" : "Board"], ["table", Table2, lang === "es" ? "Tabla" : "Table"], ["wall", LayoutGrid, "Wall"]].filter(([v]) => CANDIDATE_VIEWS.includes(v)).map(([v, Ico, label]) => {
                  const active = v === "table" ? (!wall && !board) : v === "wall" ? wall : board;
                  return (
                    <button key={v} onClick={() => { setWall(v === "wall"); setBoard(v === "board"); setBoardConfirm(null); setBoardMsg(""); }} aria-label={label} title={label}
                      style={{ ...segBtn(active), width: 40, height: 36, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <Ico size={17} strokeWidth={1.75} color={active ? BRAND : MUTED} />
                    </button>
                  );
                })}
              </div>}
            </div>
            {isAdmin && (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setShowAddFromDb(true); setShowAddCandidate(false); setEditingCandidate(null); }}
                  style={{ ...btn.ghost, flex: 1, height: 44, fontSize: 13 }}>
                  {lang === "es" ? "Desde la base" : "From database"}
                </button>
                <button onClick={() => { setShowAddCandidate(v => !v); setShowAddFromDb(false); setEditingCandidate(null); }}
                  style={{ ...btn.primary, flex: 1, height: 44, fontSize: 13 }}>
                  {"+ " + (lang === "es" ? "Añadir candidato" : "Add candidate")}
                </button>
              </div>
            )}
          </div>
        );
      }
      return (
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
      <div style={{ position: "relative", maxWidth: 320, flex: "1 1 240px" }}>
        <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}><Icon name="search" size={15} color={MUTED} /></div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.search + "..."} style={{ ..._tb, width: "100%", paddingLeft: 36, paddingRight: 12 }} onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
      </div>

        <button onClick={() => setOnlyStarred(v => !v)}
          style={{ ..._tb, background: onlyStarred ? BRAND_LIGHT : WHITE, borderColor: onlyStarred ? BRAND : LINE, padding: "0 14px", color: onlyStarred ? BRAND : TEXT, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ color: STAR, fontSize: 13, lineHeight: 1 }}>★</span>
          {lang === "es" ? "Destacados" : "Starred"}
          {Object.values(starred).filter(Boolean).length > 0 && ` (${Object.values(starred).filter(Boolean).length})`}
        </button>

        {/* "Evaluar candidato..." se ha movido a la pestaña Evaluación. */}

        {/* Añadir candidato junto a los demás controles de la barra: comparte
            alto y tamaño de fuente con el buscador y el desplegable. En la
            cabecera iba suelto y a otra escala. */}
        {isAdmin && (
          <button onClick={() => { setShowAddCandidate(v => !v); setShowAddFromDb(false); setEditingCandidate(null); }}
            aria-label={showAddCandidate ? (lang === "es" ? "Cerrar" : "Close") : undefined}
            style={showAddCandidate
              ? { ...btn.ghost, height: 38, boxSizing: "border-box", padding: "0 14px", display: "inline-flex", alignItems: "center" }
              : { ...btn.primary, height: 38, boxSizing: "border-box", lineHeight: 1, padding: "0 20px", whiteSpace: "nowrap" }}>
            {showAddCandidate ? <X size={16} strokeWidth={1.75} /> : ("+ " + (lang === "es" ? "Añadir candidato" : "Add candidate"))}
          </button>
        )}
        {isAdmin && (
          <button onClick={() => { setShowAddFromDb(v => !v); setShowAddCandidate(false); setEditingCandidate(null); }}
            style={{ ...btn.ghost, height: 38, boxSizing: "border-box", padding: "0 16px", whiteSpace: "nowrap" }}>
            {lang === "es" ? "Añadir desde la base" : "Add from database"}
          </button>
        )}

        {/* Vista y fotos a la derecha del todo: son controles de presentación,
            no de búsqueda, y mezclarlos con los filtros confundía. */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          {wall && (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {lang === "es" ? "Modo" : "Mode"}
              </span>
              <div style={{ ...segWrap, display: "inline-flex", height: 38, boxSizing: "border-box", alignItems: "center" }}>
                {[["faces", lang === "es" ? "Caras" : "Faces", lang === "es" ? "Las personas delante, sin números. Para pensar con la intuición." : "People first, no numbers. Judge with intuition."],
                  ["data", lang === "es" ? "Datos" : "Data", lang === "es" ? "Sin caras, con la evidencia. Para pensar con los datos." : "No faces, evidence only. Judge with the data."]].map(([v, label, tip]) => (
                  <button key={v} onClick={() => setWallMode(v)} title={tip}
                    style={{ ...segBtn(wallMode === v), padding: "0 16px", height: 30, transition: "all 0.12s" }}>
                    {label}
                  </button>
                ))}
              </div>

            </>
          )}
          {/* Etiqueta + iconos: una vista es un concepto visual, y el icono lo
              dice mejor que la palabra. El activo va en BRAND, no en negro:
              el sistema reserva el azul para lo seleccionable y activo. */}
          {CANDIDATE_VIEWS.length > 1 && <>
          <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {lang === "es" ? "Vista" : "View"}
          </span>
          <div style={{ ...segWrap, display: "inline-flex", height: 38, boxSizing: "border-box", alignItems: "center" }}>
            {[["board", BoardIcon, lang === "es" ? "Tablero" : "Board"], ["table", Table2, lang === "es" ? "Tabla" : "Table"], ["wall", LayoutGrid, "Wall"]].filter(([v]) => CANDIDATE_VIEWS.includes(v)).map(([v, Ico, label]) => {
              const active = v === "table" ? (!wall && !board) : v === "wall" ? wall : board;
              return (
                <button key={v} onClick={() => { setWall(v === "wall"); setBoard(v === "board"); setBoardConfirm(null); setBoardMsg(""); }} title={label}
                  style={{ ...segBtn(active), width: 40, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "background 0.12s" }}>
                  <Ico size={17} strokeWidth={1.75} color={active ? BRAND : MUTED} />
                </button>
              );
            })}
          </div>
          </>}
        </div>
      </div>
      ); })()}

      {/* Tabla en el móvil: una fila apilada por candidato (nombre, cargo ·
          empresa · fecha, fase en gris y cifras). Tocar la fila abre la ficha.
          Las acciones y sus confirmaciones son las mismas que en escritorio. */}
      {!wall && !board && isMobile && (() => {
        const sortOpts = [
          ["", lang === "es" ? "Orden del proceso" : "Pipeline order"],
          ["name", t.name], ["company", t.company], ["appDate", t.appDate],
          ["rut", lang === "es" ? "Cualificación" : "Qualification"], ["eval", lang === "es" ? "Evaluación" : "Evaluation"], ["status", t.status],
        ];
        const iconBtn = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 8, width: 40, height: 40, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxSizing: "border-box" };
        return (
          <>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0 }}>{lang === "es" ? "Ordenar" : "Sort"}</span>
              <select value={sortKey || ""} onChange={e => { const k = e.target.value; setSortKey(k || null); setSortDir(k === "rut" || k === "eval" ? "desc" : "asc"); }}
                style={{ flex: 1, minWidth: 0, height: 44, boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 16, fontFamily: "inherit", color: TEXT, background: WHITE, padding: "0 10px", outline: "none" }}>
                {sortOpts.map(([k, l]) => <option key={k || "default"} value={k}>{l}</option>)}
              </select>
              {sortKey && (
                <button onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")} aria-label={lang === "es" ? "Invertir orden" : "Reverse order"}
                  style={{ ...iconBtn, width: 44, height: 44, border: "1.5px solid " + LINE, color: TEXT, fontSize: 16, fontFamily: "inherit" }}>
                  {sortDir === "asc" ? "↑" : "↓"}
                </button>
              )}
            </div>
            <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, overflow: "hidden" }}>
              {filtered.length === 0 && (
                <div style={{ ...text.body, color: MUTED, padding: "28px 16px", textAlign: "center" }}>{lang === "es" ? "No hay candidatos que mostrar." : "No candidates to show."}</div>
              )}
              {filtered.map((c, i) => {
                const status = getStatus(c);
                const isPending = status === "pending";
                const step = confirmIdx?.id === c.id ? confirmIdx.step : null;
                const hasEvalData = c.evalManagers && c.evalManagers.length > 0;
                const rutAvg = c.scoring && Object.values(c.scoring).some(v => v > 0) ? (Object.values(c.scoring).reduce((a, b) => a + b, 0) / 4).toFixed(1) : null;
                const evalAvg = evalAvg5(c.evalManagers, c.evalScores, evalIds);
                const meta = [c.position, c.company].filter(Boolean).join(" · ");
                return (
                  <div key={c.id} style={{ padding: "12px 12px 12px 14px", borderTop: i > 0 ? `1px solid ${ROW_LINE}` : "none" }}>
                    <div onClick={() => openCandidate(c)} style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                      <div style={{ position: "relative", flexShrink: 0, marginTop: 2 }}>
                        {photoUrls[c.id] ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block" }} /> : <CandidateAvatar name={c.name} size={40} />}
                        {isPending && <span style={{ position: "absolute", top: -1, right: -1, background: UNREAD, borderRadius: "50%", width: 10, height: 10, boxShadow: `0 0 0 2px ${WHITE}` }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                        {meta && <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.4, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</div>}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          <StatusBadge status={status} t={t} hasRefs={c.refsData && c.refsData.length > 0} hasEval={hasEvalData} />
                          <span style={{ fontSize: 12, color: MUTED }}>{lang === "es" ? "Cual." : "Qual."} <strong style={{ color: rutAvg ? INK : FAINT, fontWeight: 600 }}>{rutAvg ?? "—"}</strong></span>
                          <span style={{ fontSize: 12, color: MUTED }}>Eval. <strong style={{ color: evalAvg !== null ? INK : FAINT, fontWeight: 600 }}>{evalAvg !== null ? evalAvg.toFixed(1) : "—"}</strong></span>
                          {c.appDate && <span style={{ fontSize: 12, color: MUTED }}>{c.appDate}</span>}
                        </div>
                      </div>
                      <button onClick={(e) => toggleStar(e, c.id)} aria-label={lang === "es" ? "Destacar" : "Star"}
                        style={{ background: "none", border: "none", cursor: "pointer", width: 40, height: 40, marginTop: -6, marginRight: -4, fontSize: 16, lineHeight: 1, color: starred[c.id] ? STAR : FAINT, flexShrink: 0 }}>
                        {starred[c.id] ? "★" : "☆"}
                      </button>
                    </div>

                    {(isPending || isAdmin) && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10, marginLeft: 52 }}>
                        {isPending && (
                          <>
                            <button onClick={() => { setConfirmDeleteId(null); setConfirmIdx(step === "validate" ? null : { id: c.id, step: "validate" }); }}
                              style={{ ...btn.ghost, minHeight: 40, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <Icon name="check" size={14} color={MUTED} /> {lang === "es" ? "Validar" : "Validate"}
                            </button>
                            <button onClick={() => { setConfirmDeleteId(null); setConfirmIdx(step === "reject" ? null : { id: c.id, step: "reject" }); }}
                              style={{ ...btn.ghost, minHeight: 40, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <Icon name="x" size={14} color={MUTED} /> {lang === "es" ? "Rechazar" : "Reject"}
                            </button>
                          </>
                        )}
                        {isAdmin && (
                          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                            <button aria-label={lang === "es" ? "Editar" : "Edit"} onClick={() => { setShowAddCandidate(false); setEditingCandidate(c); }} style={iconBtn}><Icon name="edit" size={15} color={MUTED} /></button>
                            <button aria-label={lang === "es" ? "Eliminar" : "Delete"} onClick={() => { setConfirmDeleteId(confirmDeleteId === c.id ? null : c.id); setConfirmIdx(null); }}
                              style={{ ...iconBtn, background: confirmDeleteId === c.id ? SURFACE : WHITE }}><Icon name="trash" size={15} color={MUTED} /></button>
                          </div>
                        )}
                      </div>
                    )}

                    {step === "validate" && (
                      <ConfirmBar danger={false} style={{ marginTop: 10 }}
                        message={lang === "es" ? <>¿Validar a <strong>{c.name}</strong>?</> : <>Validate <strong>{c.name}</strong>?</>}
                        actionLabel={<>✉ {lang === "es" ? "Confirmar y enviar email" : "Confirm + send email"}</>}
                        cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                        onConfirm={() => handleConfirmValidate(c, true)}
                        onCancel={() => setConfirmIdx(null)}>
                        {noEmailBtn(() => handleConfirmValidate(c, false))}
                      </ConfirmBar>
                    )}
                    {step === "reject" && (
                      <ConfirmBar style={{ marginTop: 10 }}
                        message={lang === "es" ? <>¿Seguro que quieres rechazar a <strong>{c.name}</strong>?</> : <>Are you sure you want to reject <strong>{c.name}</strong>?</>}
                        actionLabel={lang === "es" ? "Rechazar" : "Reject"}
                        cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                        onConfirm={() => { setConfirmIdx(null); rejectCandidate(c); }}
                        onCancel={() => setConfirmIdx(null)} />
                    )}
                    {confirmDeleteId === c.id && (
                      <ConfirmBar style={{ marginTop: 10 }}
                        message={lang === "es" ? <>¿Seguro que quieres eliminar a <strong>{c.name}</strong>? Esta acción no se puede deshacer.</> : <>Permanently delete <strong>{c.name}</strong>? This cannot be undone.</>}
                        actionLabel={lang === "es" ? "Eliminar" : "Delete"}
                        cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                        onConfirm={async () => { setConfirmDeleteId(null); await deleteCandidate(c); }}
                        onCancel={() => setConfirmDeleteId(null)} />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* Table */}
      {!wall && !board && !isMobile && (
      <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
          <thead>
            <tr style={{ background: SURFACE, borderBottom: `1.5px solid ${LINE}` }}>
              {[
                { label: t.name, key: "name" },
                { label: t.position, key: null },
                { label: t.company, key: "company" },
                { label: t.appDate, key: "appDate" },
                { label: t.docs, key: null },
                { label: lang === "es" ? "Cual." : "Qual.", key: "rut" },
                { label: "Eval.", key: "eval" },
                { label: t.status, key: "status" },
                { label: t.action, key: null },
              ].map((h, i) => (
                <th key={i} onClick={h.key ? () => handleSort(h.key) : undefined}
                  style={{ padding: "12px 16px", textAlign: h.key === "rut" || h.key === "eval" ? "center" : "left", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.5, verticalAlign: "middle", cursor: h.key ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap" }}>
                  {h.label}{h.key && <span style={{ marginLeft: 4, opacity: sortKey === h.key ? 1 : 0.3 }}>{sortKey === h.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => {
              const status = getStatus(c);
              const isPending = status === "pending";
              const isConfirming = confirmIdx?.id === c.id;
              const step = isConfirming ? confirmIdx.step : null;
              const hasEvalData = c.evalManagers && c.evalManagers.length > 0;
              return (
                <>
                  <tr key={c.id} style={{ borderBottom: isConfirming ? "none" : i < filtered.length - 1 ? `1px solid ${ROW_LINE}` : "none", transition: "background 0.1s", cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = HOVER_BG}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    onClick={(e) => { if (e.target.closest("button")) return; setInitialCandidateTab("scoring"); setBackView("candidates"); setSelectedCandidate(c); setView("candidateDetail"); }}>
                    <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <button onClick={(e) => toggleStar(e, c.id)} title={lang === "es" ? "Destacar" : "Star"}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 15, lineHeight: 1, color: starred[c.id] ? STAR : LINE, flexShrink: 0, transition: "color 0.15s" }}
                          onMouseEnter={e => { if (!starred[c.id]) e.currentTarget.style.color = STAR; }}
                          onMouseLeave={e => { if (!starred[c.id]) e.currentTarget.style.color = LINE; }}>
                          {starred[c.id] ? "★" : "☆"}
                        </button>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          {photoUrls[c.id] ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block" }} /> : <CandidateAvatar name={c.name} size={32} />}
                          {isPending && (
                            <span style={{ position: "absolute", top: -2, right: -2, background: UNREAD, borderRadius: "50%", width: 10, height: 10, boxShadow: `0 0 0 2px ${WHITE}`, zIndex: 1 }} />
                          )}
                        </div>
                        <span style={{ fontWeight: isPending ? 700 : 400, fontSize: 13, color: INK }}>{c.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: INK, fontWeight: isPending ? 700 : 400, verticalAlign: "middle" }}>{c.position}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: INK, fontWeight: isPending ? 700 : 400, verticalAlign: "middle" }}>{c.company}</td>
                    <td style={{ padding: "14px 16px", fontSize: 12, color: MUTED, fontWeight: 400, verticalAlign: "middle" }}>{c.appDate}</td>
                    <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {c.cvUrl && <span style={{ ...chip(false), display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="file" size={12} color={MUTED} /> {t.cv}</span>}
                        {c.scoring && Object.values(c.scoring).some(v=>v>0) && <span style={{ ...chip(false), display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="file" size={12} color={MUTED} /> {t.report}</span>}
                        {c.refsData && c.refsData.length > 0 && <span style={{ ...chip(false), display:"inline-flex", alignItems:"center", gap:4 }}><Icon name="users" size={12} color={MUTED} /> Refs</span>}
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", textAlign: "center", verticalAlign: "middle" }}>
                      {(() => {
                        const rutAvg = c.scoring ? parseFloat((Object.values(c.scoring).reduce((a,b)=>a+b,0)/4).toFixed(1)) : null;
                        return rutAvg !== null
                          ? <span style={{ fontWeight: 500, fontSize: 13, color: INK }}>{rutAvg}<span style={{ fontSize: 11, color: FAINT, fontWeight: 600 }}>/5</span></span>
                          : <span style={{ color: LINE, fontSize: 12 }}>—</span>;
                      })()}
                    </td>
                    <td style={{ padding: "10px 16px", textAlign: "center", verticalAlign: "middle" }}>
                      {(() => {
                        const evalAvg = evalAvg5(c.evalManagers, c.evalScores, evalIds);
                        return evalAvg !== null
                          ? <span style={{ fontWeight: 500, fontSize: 13, color: INK }}>{evalAvg.toFixed(1)}<span style={{ fontSize: 11, color: FAINT, fontWeight: 600 }}>/5</span></span>
                          : <span style={{ color: LINE, fontSize: 12 }}>—</span>;
                      })()}
                    </td>
                    <td style={{ padding: "14px 16px", verticalAlign: "middle" }}><div style={{ display: "flex", justifyContent: "flex-start" }}><StatusBadge status={status} t={t} hasRefs={c.refsData && c.refsData.length > 0} hasEval={hasEvalData} /></div></td>
                    <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          title={lang === "es" ? "Validar" : "Validate"}
                          disabled={!isPending}
                          onClick={e => { e.stopPropagation(); isPending && setConfirmIdx(isConfirming ? null : { id: c.id, step: "validate" }); }}
                          onMouseEnter={e => { if (isPending) e.currentTarget.style.background = ROW_LINE; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                          style={{ ...iconBtnTok, width: 28, height: 28, cursor: !isPending ? "default" : "pointer", transition: "background 0.12s" }}>
                          <Icon name="check" size={13} color={isPending ? MUTED : LINE} />
                        </button>
                        <button
                          title={lang === "es" ? "Rechazar" : "Reject"}
                          disabled={!isPending}
                          onClick={e => { e.stopPropagation(); isPending && setConfirmIdx(isConfirming && step === "reject" ? null : { id: c.id, step: "reject" }); }}
                          onMouseEnter={e => { if (isPending) e.currentTarget.style.background = ROW_LINE; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                          style={{ ...iconBtnTok, width: 28, height: 28, cursor: !isPending ? "default" : "pointer", transition: "background 0.12s" }}>
                          <Icon name="x" size={13} color={isPending ? MUTED : LINE} />
                        </button>
                        {isAdmin && <button title={lang === "es" ? "Editar" : "Edit"} onClick={e=>{ e.stopPropagation(); setShowAddCandidate(false); setEditingCandidate(c); }} style={{ ...iconBtnTok, width: 28, height: 28, marginLeft: 6 }}><Icon name="edit" size={13} color={MUTED} /></button>}
                        {isAdmin && <button title={lang === "es" ? "Eliminar" : "Delete"} onClick={e=>{ e.stopPropagation(); setConfirmDeleteId(confirmDeleteId===c.id?null:c.id); setConfirmIdx(null); }} style={{ ...iconBtnTok, width: 28, height: 28, marginLeft: 4, background: confirmDeleteId===c.id ? ROW_LINE : "transparent" }}><Icon name="trash" size={13} color={MUTED} /></button>}
                      </div>
                    </td>
                  </tr>

                  {/* Validate banner — confirm with or without email */}
                  {step === "validate" && (
                    <tr key={`validate-${c.id}`} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${ROW_LINE}` : "none" }}>
                      <td colSpan={9} style={{ padding: "0 16px 14px 16px" }}>
                        <ConfirmBar danger={false}
                          message={lang === "es" ? <>¿Validar a <strong>{c.name}</strong>?</> : <>Validate <strong>{c.name}</strong>?</>}
                          actionLabel={<>✉ {lang === "es" ? "Confirmar y enviar email" : "Confirm + send email"}</>}
                          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                          onConfirm={() => handleConfirmValidate(c, true)}
                          onCancel={() => setConfirmIdx(null)}>
                          {noEmailBtn(() => handleConfirmValidate(c, false))}
                        </ConfirmBar>
                      </td>
                    </tr>
                  )}

                  {/* Step: Reject banner */}
                  {step === "reject" && (
                    <tr key={`reject-${c.id}`} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${ROW_LINE}` : "none" }}>
                      <td colSpan={9} style={{ padding: "0 16px 14px 16px" }}>
                        <ConfirmBar
                          message={lang === "es" ? <>¿Seguro que quieres rechazar a <strong>{c.name}</strong>?</> : <>Are you sure you want to reject <strong>{c.name}</strong>?</>}
                          actionLabel={lang === "es" ? "Rechazar" : "Reject"}
                          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                          onConfirm={() => { setConfirmIdx(null); rejectCandidate(c); }}
                          onCancel={() => setConfirmIdx(null)} />
                      </td>
                    </tr>
                  )}

                  {/* Delete confirmation banner */}
                  {confirmDeleteId === c.id && (
                    <tr key={`delete-${c.id}`} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${ROW_LINE}` : "none" }}>
                      <td colSpan={9} style={{ padding: "0 16px 14px 16px" }}>
                        <ConfirmBar
                          message={lang === "es" ? <>¿Seguro que quieres eliminar a <strong>{c.name}</strong>? Esta acción no se puede deshacer.</> : <>Permanently delete <strong>{c.name}</strong>? This cannot be undone.</>}
                          actionLabel={lang === "es" ? "Eliminar" : "Delete"}
                          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                          onConfirm={async () => { setConfirmDeleteId(null); await deleteCandidate(c); }}
                          onCancel={() => setConfirmDeleteId(null)} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {boardMsg && (
        <div style={{ ...errorBanner, marginBottom: 14 }}>{boardMsg}</div>
      )}

      {/* ── Tablero ─────────────────────────────────────────────────────────
          Columnas por fase; arrastrar una tarjeta cambia su estado. Cada
          movimiento pasa por boardBlock (quién puede y adónde) y, si valida o
          descarta, por la misma confirmación que la tabla. Una columna que no
          acepta la tarjeta lo dice al pasar por encima, en vez de dejar que
          rebote sin explicación. */}
      {board && (() => {
        const dragged = position.candidates.find(x => String(x.id) === String(boardDragId));
        const confirmC = boardConfirm && position.candidates.find(x => String(x.id) === String(boardConfirm.id));
        const qualAvg = (c) => {
          const v = c.scoring ? Object.values(c.scoring) : [];
          return v.some(x => x > 0) ? v.reduce((s, x) => s + x, 0) / v.length : null;
        };
        // ── Tablero en el móvil ─────────────────────────────────────────
        // Una columna cada vez, elegida en el selector de arriba (con su
        // recuento). Arrastrar no existe en táctil: cada tarjeta lleva UNA
        // acción, la que toca en su fase (validar o pasar a la siguiente), y
        // en «⋯» el resto: mover a otra fase, subir/bajar y descartar (24/10).
        // Todo pasa por boardBlock/boardDrop (mismas confirmaciones) y
        // boardReorder (el mismo guardado).
        // Acciones de una tarjeta (móvil y escritorio): la que toca en su fase
        // (validar o pasar a la siguiente) y el resto para el menú ⋯
        const cardActions = (c, curKey, cards, idx) => {
          const targets = BOARD_COLS.filter(col => col.key !== curKey && !boardBlock(c, col.key));
          const next = { pending: "validated", validated: "interview", interview: "references" }[curKey];
          const primary = next && targets.find(col => col.key === next);
          const menu = [
            ...targets.filter(col => col !== primary && col.key !== "rejected").map(col => ({ label: `${lang === "es" ? "Mover a" : "Move to"} ${lang === "es" ? col.es : col.en}`, onClick: () => boardDrop(c, col.key) })),
            cards.length > 1 && idx > 0 && { label: lang === "es" ? "Subir" : "Move up", onClick: () => { setBoardMsg(""); boardReorder(cards, c, cards[idx - 1]); } },
            cards.length > 1 && idx < cards.length - 1 && { label: lang === "es" ? "Bajar" : "Move down", onClick: () => { setBoardMsg(""); boardReorder(cards, c, cards[idx + 2] || null); } },
            targets.some(col => col.key === "rejected") && { label: lang === "es" ? "Descartar" : "Reject", danger: true, onClick: () => boardDrop(c, "rejected") },
          ].filter(Boolean);
          const primaryLabel = primary && (primary.key === "validated" ? (lang === "es" ? "Validar" : "Validate") : `${lang === "es" ? "Pasar a" : "Move to"} ${lang === "es" ? primary.es : primary.en}`);
          return { primary, primaryLabel, menu };
        };
        if (isMobile) {
          const colCards = (key) => sortBoard(filtered.filter(c => phaseOf(c) === key));
          const cur = BOARD_COLS.find(col => col.key === boardCol) || BOARD_COLS[0];
          const cards = colCards(cur.key);
          const muted = cur.key === "rejected";
          return (
            <div>
              {/* Fase como fila que abre el selector del teléfono (24/10) */}
              <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, overflow: "hidden", marginBottom: 12 }}>
                <SelectRow first label={lang === "es" ? "Fase" : "Stage"} display={`${lang === "es" ? cur.es : cur.en} · ${cards.length}`} value={cur.key}
                  options={BOARD_COLS.map(col => [col.key, `${lang === "es" ? col.es : col.en} · ${colCards(col.key).length}`])}
                  onChange={v => { setBoardCol(v); setBoardConfirm(null); }} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {cards.map((c, idx) => {
                  const q = qualAvg(c);
                  const ev = evalAvg5(c.evalManagers, c.evalScores, evalIds);
                  const { primary, primaryLabel, menu } = cardActions(c, cur.key, cards, idx);
                  const confirming = confirmC && String(confirmC.id) === String(c.id) ? boardConfirm.step : null;
                  return (
                    <div key={c.id} style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: 12, opacity: muted ? 0.8 : 1 }}>
                      <div onClick={() => openCandidate(c)} style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                        <div style={{ flexShrink: 0 }}>
                          {photoUrls[c.id]
                            ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block" }} />
                            : <CandidateAvatar name={c.name} size={40} />}
                        </div>
                        <div style={{ minWidth: 0, flex: 1, alignSelf: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.3, display: "flex", gap: 4, alignItems: "baseline" }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                            {starred[c.id] && <span style={{ color: STAR, fontSize: 13, flexShrink: 0 }}>★</span>}
                          </div>
                          {(c.position || c.company) && (
                            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.4, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {[c.position, c.company].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </div>
                        <MoreMenu items={menu} label={lang === "es" ? "Más acciones" : "More actions"} width={220} />
                      </div>

                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 10 }}>
                        <span style={{ ...text.body, fontSize: 12, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {q !== null
                            ? <>{lang === "es" ? "Cualificación" : "Qualification"} <strong style={{ color: INK }}>{q.toFixed(1)}</strong></>
                            : <span style={{ color: MUTED }}>{lang === "es" ? "Sin cualificar" : "Not scored"}</span>}
                          {/* La evaluación del equipo, solo si la hay */}
                          {ev !== null && <> · {lang === "es" ? "Entrevistas" : "Interviews"} <strong style={{ color: INK }}>{ev.toFixed(1)}</strong></>}
                        </span>
                        {primary && (
                          <button onClick={() => boardDrop(c, primary.key)} style={{ ...btn.primary, height: 40, padding: "0 16px", flexShrink: 0, whiteSpace: "nowrap" }}>
                            {primaryLabel}
                          </button>
                        )}
                      </div>

                      {confirming === "validate" && (
                        <ConfirmBar danger={false} style={{ marginTop: 10 }}
                          message={lang === "es" ? <>¿Validar a <strong>{c.name}</strong>?</> : <>Validate <strong>{c.name}</strong>?</>}
                          actionLabel={<>✉ {lang === "es" ? "Confirmar y enviar email" : "Confirm + send email"}</>}
                          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                          onConfirm={() => { handleConfirmValidate(c, true); setBoardConfirm(null); }}
                          onCancel={() => setBoardConfirm(null)}>
                          {noEmailBtn(() => { handleConfirmValidate(c, false); setBoardConfirm(null); })}
                        </ConfirmBar>
                      )}
                      {confirming === "reject" && (
                        <ConfirmBar style={{ marginTop: 10 }}
                          message={lang === "es" ? <>¿Seguro que quieres descartar a <strong>{c.name}</strong>?</> : <>Are you sure you want to reject <strong>{c.name}</strong>?</>}
                          actionLabel={lang === "es" ? "Descartar" : "Reject"}
                          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                          onConfirm={() => { rejectCandidate(c); setBoardConfirm(null); }}
                          onCancel={() => setBoardConfirm(null)} />
                      )}
                    </div>
                  );
                })}
                {cards.length === 0 && (
                  <div style={{ background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 14, ...text.body, color: MUTED, textAlign: "center", padding: "24px 12px" }}>{lang === "es" ? "Nadie en esta fase" : "No one at this stage"}</div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div>
            {confirmC && boardConfirm.step === "validate" && (
              <ConfirmBar danger={false} style={{ marginBottom: 14 }}
                message={lang === "es" ? <>¿Validar a <strong>{confirmC.name}</strong>?</> : <>Validate <strong>{confirmC.name}</strong>?</>}
                actionLabel={<>✉ {lang === "es" ? "Confirmar y enviar email" : "Confirm + send email"}</>}
                cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                onConfirm={() => { handleConfirmValidate(confirmC, true); setBoardConfirm(null); }}
                onCancel={() => setBoardConfirm(null)}>
                {noEmailBtn(() => { handleConfirmValidate(confirmC, false); setBoardConfirm(null); })}
              </ConfirmBar>
            )}
            {confirmC && boardConfirm.step === "reject" && (
              <ConfirmBar style={{ marginBottom: 14 }}
                message={lang === "es" ? <>¿Seguro que quieres descartar a <strong>{confirmC.name}</strong>?</> : <>Are you sure you want to reject <strong>{confirmC.name}</strong>?</>}
                actionLabel={lang === "es" ? "Descartar" : "Reject"}
                cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
                onConfirm={() => { rejectCandidate(confirmC); setBoardConfirm(null); }}
                onCancel={() => setBoardConfirm(null)} />
            )}
            {/* Descartados, plegada por defecto (31/10): solo el nombre y la cifra,
                así las cuatro fases caben sin barra horizontal y los nombres
                no se cortan. Se despliega con «Ver» y sigue aceptando tarjetas. */}
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${BOARD_COLS.length - 1}, minmax(200px, 1fr)) ${showRejected ? "minmax(200px, 1fr)" : "130px"}`, gap: 14, overflowX: "auto", paddingBottom: 6, alignItems: "start" }}>
              {BOARD_COLS.map(col => {
                const cards = sortBoard(filtered.filter(c => phaseOf(c) === col.key));
                const isOver = boardOver === col.key && dragged && phaseOf(dragged) !== col.key;
                const block = isOver ? boardBlock(dragged, col.key) : null;
                const muted = col.key === "rejected";
                const folded = muted && !showRejected;
                return (
                  <div key={col.key}
                    onDragOver={e => { if (dragged && !boardBlock(dragged, col.key)) e.preventDefault(); if (boardOver !== col.key) setBoardOver(col.key); }}
                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setBoardOver(null); }}
                    onDrop={e => { e.preventDefault(); if (dragged && phaseOf(dragged) === col.key) boardReorder(cards, dragged, null); else boardDrop(dragged, col.key); setBoardOver(null); setBoardOverCard(null); setBoardDragId(null); }}
                    style={{
                      background: muted ? NEUTRAL_BG : SURFACE, borderRadius: 14, padding: 12, minHeight: 160,
                      border: `1.5px ${isOver ? "dashed" : "solid"} ${isOver ? (block ? DANGER_LINE : BRAND) : LINE}`,
                      transition: "border-color 0.12s",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 4px 10px" }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: muted ? MUTED : INK }}>
                        {lang === "es" ? col.es : col.en}
                      </span>
                      {/* Pendientes: el número con el círculo rojo de avisos, es lo que toca hacer (15/10) */}
                      {col.key === "pending" && pulsePending && cards.length > 0 && (
                        <style>{`@keyframes rt-pend-wave{0%{box-shadow:0 0 0 0 ${UNREAD}73}70%{box-shadow:0 0 0 12px ${UNREAD}00}100%{box-shadow:0 0 0 0 ${UNREAD}00}}@media (prefers-reduced-motion: reduce){.rt-pend-wave{animation:none!important}}`}</style>
                      )}
                      <span className={col.key === "pending" && pulsePending ? "rt-pend-wave" : undefined}
                        style={col.key === "pending" && cards.length > 0 ? { ...unreadBadge, ...(pulsePending ? { animation: "rt-pend-wave 1.4s ease-out .4s 2" } : null) } : { fontSize: 11, fontWeight: 700, color: MUTED, background: WHITE, border: `1px solid ${LINE}`, borderRadius: 999, minWidth: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px", boxSizing: "border-box" }}>{cards.length}</span>
                    </div>

                    {isOver && block && (
                      <div style={{ ...errorBanner, fontSize: 12, padding: "8px 10px", marginBottom: 8 }}>{block}</div>
                    )}

                    {muted && cards.length > 0 && (
                      <button type="button" onClick={() => setShowRejected(v => !v)} style={{ ...linkBtn, color: MUTED, fontWeight: 600, padding: "0 4px 8px" }}>
                        {showRejected ? (lang === "es" ? "Ocultar" : "Hide") : (lang === "es" ? "Ver" : "Show")}
                      </button>
                    )}
                    <div style={{ display: folded ? "none" : "flex", flexDirection: "column", gap: 8 }}>
                      {cards.map((c, idx) => {
                        const q = qualAvg(c);
                        const ev = evalAvg5(c.evalManagers, c.evalScores, evalIds);
                        const { primary, primaryLabel, menu } = cardActions(c, col.key, cards, idx);
                        return (
                          <div key={c.id} draggable className="rt-bcard" tabIndex={0}
                            onKeyDown={e => { if (e.key === "Enter" && e.target === e.currentTarget) openCandidate(c); }}
                            onDragStart={e => { setBoardDragId(c.id); setBoardMsg(""); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(c.id)); }}
                            onDragEnd={() => { setBoardDragId(null); setBoardOver(null); setBoardOverCard(null); }}
                            onDragOver={e => { if (dragged && phaseOf(dragged) === col.key && String(dragged.id) !== String(c.id)) { e.preventDefault(); if (boardOverCard !== c.id) setBoardOverCard(c.id); } }}
                            onDrop={e => { if (dragged && phaseOf(dragged) === col.key) { e.preventDefault(); e.stopPropagation(); boardReorder(cards, dragged, c); setBoardOver(null); setBoardOverCard(null); setBoardDragId(null); } }}
                            onClick={() => { setInitialCandidateTab("scoring"); setBackView("candidates"); setSelectedCandidate(c); setView("candidateDetail"); }}
                            style={{
                              background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 12, padding: 12, cursor: "grab",
                              ...(boardOverCard === c.id && dragged && phaseOf(dragged) === col.key ? { borderTop: `3px solid ${BRAND}` } : {}),
                              opacity: String(boardDragId) === String(c.id) ? 0.4 : muted ? 0.75 : 1,
                              boxShadow: SHADOW_SM, transition: "opacity 0.12s, box-shadow 0.12s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = SHADOW_POP}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = SHADOW_SM}>
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                              <div style={{ flexShrink: 0 }}>
                                {photoUrls[c.id]
                                  ? <img src={photoUrls[c.id]} alt={c.name} draggable={false} style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block" }} />
                                  : <CandidateAvatar name={c.name} size={38} />}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: INK, lineHeight: 1.3, display: "flex", gap: 4, alignItems: "baseline" }}>
                                  {/* El nombre entero, en dos líneas si hace falta (31/10): al founder le importa el nombre */}
                                  <span style={{ overflowWrap: "anywhere" }}>{c.name}</span>
                                  {starred[c.id] && <span style={{ color: STAR, fontSize: 12, flexShrink: 0 }}>★</span>}
                                </div>
                                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.45, marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {[c.position, c.company].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                              {menu.length > 0 && <div className="rt-bact" style={{ margin: "-6px -6px 0 0" }}><MoreMenu items={menu} label={lang === "es" ? "Más acciones" : "More actions"} size={30} width={210} /></div>}
                            </div>
                            <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              {q !== null
                                ? <span style={chip(false)}>{lang === "es" ? "Cual." : "Qual."} {q.toFixed(1)}<span style={{ color: MUTED, fontWeight: 600 }}>/5</span></span>
                                : <span style={chip(false)}>{lang === "es" ? "Sin cualificar" : "Not scored"}</span>}
                              {/* La evaluación del equipo, mismo formato, solo si la hay */}
                              {ev !== null && <span style={chip(false)}>Eval. {ev.toFixed(1)}<span style={{ color: MUTED, fontWeight: 600 }}>/5</span></span>}
                              {primary && (
                                <button className="rt-bact" onClick={e => { e.stopPropagation(); boardDrop(c, primary.key); }}
                                  style={{ ...btn.primary, ...btnSm, marginLeft: "auto", padding: "0 12px", height: 28, fontSize: 12 }}>{primaryLabel}</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {cards.length === 0 && !isOver && (
                        <div style={{ ...text.empty, textAlign: "center", padding: "18px 6px" }}>{lang === "es" ? "Nadie en esta fase" : "No one at this stage"}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Las acciones de cada tarjeta aparecen con el ratón encima o con el foco del teclado */}
            <style>{`.rt-bcard .rt-bact { visibility: hidden; } .rt-bcard:hover .rt-bact, .rt-bcard:focus-within .rt-bact { visibility: visible; } .rt-bcard:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 2px; }`}</style>
            <p style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>
              {isAdmin
                ? (lang === "es" ? "Arrastra para cambiar de fase, o usa el botón de cada tarjeta. Validar y descartar piden confirmación." : "Drag to change stage, or use each card's button. Validating and rejecting ask for confirmation.")
                : (lang === "es" ? "Arrastra un candidato pendiente a Validados o Descartados, o usa el botón de su tarjeta. Las demás fases las gestiona Rutment." : "Drag a pending candidate to Validated or Rejected, or use the button on its card. Other stages are managed by Rutment.")}
            </p>
          </div>
        );
      })()}

      {/* ── Wall ──────────────────────────────────────────────────────────
          Equivalente digital de repartir las fotos de los finalistas por una
          sala. El valor no está en ver las caras grandes: está en poder
          moverlas y compararlas. Por eso son arrastrables.
          El orden NO se guarda, a propósito: es una herramienta para pensar,
          no un artefacto que mantener. Al salir se pierde, como en la sala. */}
      {wall && (() => {
        const ordered = wallOrder.length
          ? [...filtered].sort((a, b) => {
              const ia = wallOrder.indexOf(a.id), ib = wallOrder.indexOf(b.id);
              return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
            })
          : filtered;

        const move = (fromId, toId) => {
          if (fromId === toId) return;
          const base = ordered.map(c => c.id);
          const from = base.indexOf(fromId), to = base.indexOf(toId);
          if (from === -1 || to === -1) return;
          base.splice(to, 0, base.splice(from, 1)[0]);
          setWallOrder(base);
        };

        const faces = wallMode === "faces";

        if (filtered.length === 0) return (
          <div style={{ background: SURFACE, border: "1.5px dashed " + LINE, borderRadius: 14, padding: 48, textAlign: "center", ...text.body, color: MUTED }}>
            {lang === "es" ? "No hay candidatos que mostrar." : "No candidates to show."}
          </div>
        );

        return (
          <>
            {/* Móvil: sin la ayuda de arrastrar (en táctil no se arrastra) */}
            {!isMobile && <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>
              {lang === "es"
                ? (faces
                    ? "Arrastra para agrupar y comparar. Cambia a Datos para ver la evidencia sin las caras."
                    : "Arrastra para agrupar y comparar. Cambia a Caras para decidir sin los números.")
                : (faces
                    ? "Drag to group and compare. Switch to Data to see the evidence without faces."
                    : "Drag to group and compare. Switch to Faces to decide without the numbers.")}
            </div>}
            <div style={isMobile
              ? { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, alignItems: "start" }
              : { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 18, alignItems: "start" }}>
              {ordered.map(c => {
                const rutAvg = c.scoring ? (Object.values(c.scoring).reduce((a, b) => a + b, 0) / 4).toFixed(1) : null;
                // evalScores está anidado por evaluador con 8 criterios cada
                // uno: promediarlo en plano daba NaN. Mismo cálculo que la tabla.
                const evalAvg = evalAvg5(c.evalManagers, c.evalScores, evalIds);
                // El radar pinta una fuente u otra según el conmutador. Si se
                // pide la evaluación y no existe, se avisa en lugar de pintar ceros.
                const evalScoring = (() => {
                  if (!c.evalManagers || c.evalManagers.length === 0) return null;
                  return evalByVector(c.evalManagers, c.evalScores, evalIds);
                })();
                const shown = wallScore === "eval" ? evalScoring : (c.scoring || null);
                const dim = lang === "es" ? [
                  ["T", shown?.technical, "Encaje técnico", "Indica si el candidato ha desempeñado este rol anteriormente y si dispone de los conocimientos necesarios sobre el puesto, el sector, el mercado, la red de contactos, etc."],
                  ["S", shown?.soft, "Habilidades blandas", "Indica si el candidato posee las habilidades interpersonales requeridas para esta posición teniendo en cuenta su nivel de seniority."],
                  ["C", shown?.cultural, "Encaje cultural", "Mide el encaje del candidato con el equipo, la dinámica y la situación actual de la empresa."],
                  ["G", shown?.growth, "Potencial de crecimiento", "Mide la capacidad del candidato de adaptarse con éxito a distintos roles, empresas y equipos, asumiendo nuevas responsabilidades a lo largo de su carrera."],
                ] : [
                  ["T", shown?.technical, "Technical fit", "Indicates whether the candidate has performed this role before and has all the knowledge required for the position — including sector expertise, market understanding, and relevant network."],
                  ["S", shown?.soft, "Soft skills", "Indicates whether the candidate has the interpersonal and behavioural skills required for this position at their level of seniority."],
                  ["C", shown?.cultural, "Cultural fit", "Measures how well the candidate's values, working style and personality align with the company's team dynamics and current situation."],
                  ["G", shown?.growth, "Growth potential", "Measures the candidate's ability to successfully adapt to different roles, companies and teams, taking on new responsibilities and growing throughout their career."],
                ];
                const salary = c.salary
                  ? (/[€$£¥]|\b(eur|usd|gbp|chf)\b/i.test(String(c.salary)) || !/\d/.test(String(c.salary)) ? c.salary : c.salary + " €")
                  : null;

                // Las dos caras viven en la MISMA tarjeta, de altura fija: al
                // conmutar solo gira, no se recalcula el diseño. Eso elimina el
                // salto de tamaño que provocaba la barra de scroll al aparecer.
                const faceBase = {
                  position: "absolute", inset: 0, padding: isMobile ? 10 : 18,
                  backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
                  background: WHITE, border: `1.5px solid ${dragId === c.id ? BRAND : LINE}`,
                  borderRadius: 16, display: "flex", flexDirection: "column",
                };

                return (
                  <div key={c.id}
                    draggable={!isMobile}
                    onDragStart={() => setDragId(c.id)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => { move(dragId, c.id); setDragId(null); }}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => { setBackView("candidates"); setSelectedCandidate(c); setView("candidateDetail"); }}
                    style={{ height: isMobile ? 270 : 320, perspective: 1200, cursor: "grab", opacity: dragId === c.id ? 0.5 : 1, transition: "opacity 0.15s" }}>

                    <div style={{ position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d", transition: "transform 0.55s cubic-bezier(0.4, 0.0, 0.2, 1)", transform: faces ? "rotateY(0deg)" : "rotateY(180deg)" }}>

                      {/* ── CARA: la persona delante, nada que leer ──────── */}
                      <div style={faceBase}>
                        <div style={{ position: "relative", flex: 1, borderRadius: 12, overflow: "hidden", background: photoUrls[c.id] ? NEUTRAL_BG : NEUTRAL_BG, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                          {photoUrls[c.id]
                            ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                            : <span style={{ color: TEXT, fontWeight: 800, fontSize: WALL_INITIAL }}>{c.name?.[0]?.toUpperCase()}</span>}
                          <span onClick={(e) => toggleStar(e, c.id)}
                            style={{ position: "absolute", top: 8, right: 10, fontSize: 22, lineHeight: 1, color: starred[c.id] ? STAR : WHITE, cursor: "pointer", textShadow: SHADOW_SM, ...(isMobile ? { top: 0, right: 0, padding: 10 } : null) }}>
                            {starred[c.id] ? "★" : "☆"}
                          </span>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: INK, textAlign: "center", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: MUTED, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.position} · {c.company}</div>
                      </div>

                      {/* ── DORSO: sin cara, solo la evidencia ───────────── */}
                      <div style={{ ...faceBase, transform: "rotateY(180deg)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <span onClick={(e) => toggleStar(e, c.id)}
                            style={{ fontSize: 15, lineHeight: 1, color: starred[c.id] ? STAR : LINE, cursor: "pointer", ...(isMobile ? { padding: 8, margin: -8, fontSize: 16 } : null) }}>
                            {starred[c.id] ? "★" : "☆"}
                          </span>
                          <StatusBadge status={candidateStatuses[c.id] || c.status} t={t} hasRefs={c.references >= 1} hasEval={(c.evalManagers || []).length > 0 && c.evalUpdatedAt} />
                        </div>

                        <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 2, lineHeight: 1.3 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: MUTED, marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.position} · {c.company}</div>

                        {/* Los cuatro valores en cuadros: a este tamaño un radar
                            no comunica —cuatro rombos casi idénticos entre
                            tarjetas—, y los números se comparan de un vistazo.
                            El tooltip flota encima, con el texto completo. */}
                        <div style={{ position: "relative", display: "flex", gap: 6, marginBottom: 12 }}>
                          {dim.map(([k, v, name, tip], idx) => (
                            <div key={k}
                              onMouseEnter={isMobile ? undefined : () => setDimTip({ key: c.id + k, name, tip, value: v, idx })}
                              // Móvil: sin hover, se toca el cuadro para ver (o cerrar) su explicación
                              onClick={isMobile ? (e) => { e.stopPropagation(); setDimTip(dimTip?.key === c.id + k ? null : { key: c.id + k, name, tip, value: v, idx }); } : undefined}
                              onMouseLeave={isMobile ? undefined : () => setDimTip(null)}
                              style={{ flex: 1, textAlign: "center", background: dimTip?.key === c.id + k ? BRAND_LIGHT : SURFACE, borderRadius: 8, padding: "7px 0", cursor: "help", transition: "background 0.12s" }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: v ? INK : LINE }}>{v ?? "–"}</div>
                              <div style={{ fontSize: 11, color: dimTip?.key === c.id + k ? BRAND : MUTED, fontWeight: 700 }}>{k}</div>
                            </div>
                          ))}

                        </div>

                        {/* El hueco libre de la tarjeta: aquí el texto cabe
                            entero y no tapa el nombre ni las notas. */}
                        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                          {dimTip && dimTip.key.startsWith(c.id) && (
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 5 }}>
                                {dimTip.name} <span style={{ color: INK }}>{dimTip.value ?? "–"}</span><span style={{ color: FAINT, fontSize: 11 }}>/5</span>
                              </div>
                              <div style={{ fontSize: 11, color: TEXT, lineHeight: 1.5 }}>{dimTip.tip}</div>
                            </div>
                          )}
                        </div>

                        <div>
                          {/* Clicar una de las dos cambia qué puntuación pintan
                              los cuadros de arriba, en TODAS las tarjetas. El
                              conmutador vive donde ya estaba el dato. */}
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, borderTop: `1px solid ${ROW_LINE}`, paddingTop: 10 }}>
                            {[["qual", lang === "es" ? "Cual." : "Qual.", rutAvg], ["eval", "Eval.", evalAvg !== null ? evalAvg.toFixed(1) : null]].map(([k, label, val]) => (
                              <span key={k} onClick={(e) => { e.stopPropagation(); setWallScore(k); }}
                                title={k === "qual" ? (lang === "es" ? "Ver la puntuación de Rutment" : "Show Rutment scoring") : (lang === "es" ? "Ver la evaluación de la empresa" : "Show company evaluation")}
                                style={{ cursor: "pointer", color: wallScore === k ? BRAND : MUTED, fontWeight: wallScore === k ? 700 : 500, ...(isMobile ? { padding: "6px 0" } : null) }}>
                                {label} <strong style={{ color: wallScore === k ? BRAND : MUTED, fontSize: 13 }}>{val ?? "–"}</strong>
                              </span>
                            ))}
                          </div>
                          <div style={{ fontSize: 11, color: salary ? MUTED : FAINT, marginTop: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.salary || ""}>
                            {salary || (lang === "es" ? "Sin salario" : "No salary")}
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}
      </>)}
    </div>
  );
};

export default CandidatesView;
